import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { getResultTextColor, getResultLabel, shouldShowResult } from '@/utils/checkResultColors';
import { 
  Truck, Users, FileCheck, Clock, CheckCircle, Eye, FileText, 
  BarChart3, User, RefreshCw, LogOut, Filter, Search, Send,
  Play, MoreVertical, RotateCcw, MapPin, Shield, CreditCard,
  Briefcase, GraduationCap, UserCheck, PlusCircle, Timer, Calendar,
  AlertTriangle, CheckSquare, X, Upload, Share2, ExternalLink, Copy, UserPlus, FileEdit, Download, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';
import NotificationCenter from '@/components/shared/NotificationCenter';
import VendorFieldAgentReport from '@/components/reports/VendorFieldAgentReport';
import BulkDocumentUpload from '@/components/shared/BulkDocumentUpload';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import VerificationUpdateDialog from '@/components/shared/VerificationUpdateDialog';
import { formatDateDDMonYYYY } from '@/utils/textFormatting';

const CHECK_TYPES = [
  { value: 'address_check', label: 'Address Check' },
  { value: 'criminal_court_check', label: 'Criminal Court Check' },
  { value: 'criminal_advocate_check', label: 'Criminal Advocate Check' },
  { value: 'id_check', label: 'ID Check' },
  { value: 'employment_check', label: 'Employment Check' },
  { value: 'education_check', label: 'Education Check' },
  { value: 'reference_check', label: 'Reference Check' },
  { value: 'professional_reference', label: 'Professional Reference' },
  { value: 'personal_reference', label: 'Personal Reference' },
  { value: 'credit_check', label: 'Credit Check' },
  { value: 'drug_test', label: 'Drug Test' },
  { value: 'global_database', label: 'Global Database' },
];

const CHECK_TYPE_ICONS = {
  address_check: MapPin,
  criminal_court_check: Shield,
  criminal_advocate_check: Shield,
  id_check: CreditCard,
  employment_check: Briefcase,
  education_check: GraduationCap,
  reference_check: UserCheck,
  professional_reference: UserCheck,
  personal_reference: UserCheck,
  credit_check: CreditCard,
  drug_test: FileCheck,
  global_database: FileCheck,
};

// Default TAT values (will be overridden by API values from Check Types Management)
const DEFAULT_CHECK_TAT = {
  address_check: 5,
  employment_check: 7,
  education_check: 10,
  id_check: 3,
  criminal_court_check: 7,
  criminal_advocate_check: 5,
  credit_check: 3,
  reference_check: 5,
  professional_reference: 5,
  personal_reference: 5,
  drug_test: 5,
  global_database: 3,
};

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  assigned: 'bg-blue-100 text-blue-800',
  agent_assigned: 'bg-blue-100 text-blue-800',
  vendor_assigned: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  verified: 'bg-green-100 text-green-800',
  clear: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  completed: 'bg-emerald-100 text-emerald-800',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
};

export default function VendorManagementDashboard({ user, onLogout, onSwitchAccount }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [checkTypeTAT, setCheckTypeTAT] = useState(DEFAULT_CHECK_TAT); // TAT values from Check Types Management
  
  // Pagination
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filters
  const [filters, setFilters] = useState({
    clientId: 'all',
    checkType: 'all',
    status: 'all',
    tatStatus: 'all',
    vendorId: 'all',
    agentId: 'all',
    fieldStatus: 'all',
    state: 'all',
    educationType: 'all',
    dateFilterType: 'latest',
    search: '',
  });

  // Completed-tab independent Vendor filter (May 24, 2026) — overrides
  // the Dashboard's filters.vendorId when on the Completed tab so the
  // user can scope completed work to ANY vendor regardless of
  // dashboard state. 'inherit' means use whatever the dashboard has.
  const [completedVendorFilter, setCompletedVendorFilter] = useState('inherit');
  
  // Bulk Upload
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  
  // Field Agent Management
  const [fieldAgents, setFieldAgents] = useState([]);
  const [showAssignFieldAgent, setShowAssignFieldAgent] = useState(false);
  const [fieldAgentForm, setFieldAgentForm] = useState({ name: '', phone: '', pincode: '' });
  const [selectedCheckForFA, setSelectedCheckForFA] = useState(null);
  const [copiedText, setCopiedText] = useState(null); // Show copied text for manual copy
  
  // Field Verification View
  const [showFieldVerificationView, setShowFieldVerificationView] = useState(false);
  const [fieldVerificationData, setFieldVerificationData] = useState(null);
  const [fieldVerificationPhotos, setFieldVerificationPhotos] = useState([]);
  
  // Photo Preview Modal
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);
  
  // Dialog states
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [showCheckDetails, setShowCheckDetails] = useState(false);
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [drillDownType, setDrillDownType] = useState(null);
  const [showRaiseInsuff, setShowRaiseInsuff] = useState(false);
  const [insuffData, setInsuffData] = useState({ reason: '', details: '' });

  // Check Details state
  const [checkDetailData, setCheckDetailData] = useState(null);
  const [checkDocuments, setCheckDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [selectedSubcheck, setSelectedSubcheck] = useState(null);
  const [uploadingReport, setUploadingReport] = useState(false);

  // Multiselect for bulk initiation
  const [selectedChecks, setSelectedChecks] = useState([]);
  const [showBulkInitiateDialog, setShowBulkInitiateDialog] = useState(false);
  const [bulkInitiateLoading, setBulkInitiateLoading] = useState(false);
  const [bulkInitiateAutoAssign, setBulkInitiateAutoAssign] = useState(false);
  const [bulkInitiateVendorId, setBulkInitiateVendorId] = useState('');
  const [bulkSpecialInstructions, setBulkSpecialInstructions] = useState('');
  // Bulk Field Agent Assignment
  const [bulkFieldAgentForm, setBulkFieldAgentForm] = useState({ name: '', phone: '', pincode: '' });
  const [showBulkFieldAgentSection, setShowBulkFieldAgentSection] = useState(true);
  
  // Transfer to Vendor Team
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferAgentId, setTransferAgentId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [vendorTeamAgents, setVendorTeamAgents] = useState([]);

  // Manual Urgent TAT Reminder (May 22, 2026) — per-row + bulk action.
  // Server groups checks by vendor and sends a "URGENT/PENALTY" mail
  // using the same table format as the scheduled daily mail.
  const [showUrgentReminderDialog, setShowUrgentReminderDialog] = useState(false);
  const [urgentReminderSingleCheck, setUrgentReminderSingleCheck] = useState(null); // null = bulk
  const [urgentReminderSending, setUrgentReminderSending] = useState(false);
  const [urgentReminderResult, setUrgentReminderResult] = useState(null);

  // Export functionality
  const [exportDateFilter, setExportDateFilter] = useState('latest');
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  // Extra Cost Request
  const [showExtraCostDialog, setShowExtraCostDialog] = useState(false);
  const [extraCostCheck, setExtraCostCheck] = useState(null);
  const [extraCostAmount, setExtraCostAmount] = useState('');
  const [extraCostRemarks, setExtraCostRemarks] = useState('');
  const [extraCostLoading, setExtraCostLoading] = useState(false);

  // Return to Ops Manager
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnData, setReturnData] = useState({ reason: '', notes: '' });

  // Field Agent Management
  const [showFieldAgentManagement, setShowFieldAgentManagement] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [newPincode, setNewPincode] = useState('');
  const [showAddAgentDialog, setShowAddAgentDialog] = useState(false);
  const [newAgentForm, setNewAgentForm] = useState({ name: '', phone: '', pincodes: '' });

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
    { id: 'my-checks', label: 'My Assigned Checks', icon: FileCheck, color: 'purple', bgColor: 'bg-purple-50', textColor: 'text-purple-700' },
    { id: 'all-checks', label: 'All Checks', icon: Eye, color: 'emerald', bgColor: 'bg-emerald-50', textColor: 'text-emerald-700' },
    { id: 'completed', label: 'Completed', icon: CheckCircle, color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-700' },
    { id: 'client-tat', label: 'Client TAT Summary', icon: Clock, color: 'amber', bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
    { id: 'field-agents', label: 'Field Agents', icon: Users, color: 'indigo', bgColor: 'bg-indigo-50', textColor: 'text-indigo-700' },
    { id: 'reports', label: 'Reports', icon: BarChart3, color: 'orange', bgColor: 'bg-orange-50', textColor: 'text-orange-700' },
  ];

  // Helper to get check type string
  const getCheckTypeString = (checkType) => {
    if (typeof checkType === 'string') return checkType;
    if (checkType && typeof checkType === 'object') return checkType.check_type || checkType.id || 'unknown';
    return 'unknown';
  };

  // Fetch check types with TAT from Operations Manager settings
  const fetchCheckTypeTAT = useCallback(async () => {
    try {
      const res = await axios.get('/check-types');
      const tatMap = {};
      (res.data || []).forEach(ct => {
        if (ct.id && ct.default_tat) {
          tatMap[ct.id] = ct.default_tat;
        }
      });
      // Merge with defaults (API values take precedence)
      setCheckTypeTAT(prev => ({ ...prev, ...tatMap }));
    } catch (error) {
      console.log('Using default TAT values');
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [checksRes, clientsRes, vendorsRes, fieldAgentsRes] = await Promise.all([
        axios.get('/team/checks'),
        axios.get('/clients'),
        axios.get('/subvendors').catch(() => axios.get('/vendors')).catch(() => ({ data: [] })),
        axios.get('/vendor/field-agents').catch(() => ({ data: [] })),
      ]);
      
      let checksData = checksRes.data?.checks || checksRes.data || [];
      
      // Enrich with TAT calculation using values from Check Types Management
      checksData = checksData.map(check => {
        const checkType = getCheckTypeString(check.check_type);
        // Priority: check.tat_days (from assignment) > checkTypeTAT (from Ops Manager settings) > default
        const clientTat = check.tat_days || checkTypeTAT[checkType] || DEFAULT_CHECK_TAT[checkType] || 7;
        const agentTat = Math.max(clientTat - 2, 1); // Agent TAT = Client TAT - 2 days
        
        let daysElapsed = 0;
        let isWithinTat = true;
        
        if (check.assigned_at || check.vendor_assigned_at) {
          const assignedDate = new Date(check.vendor_assigned_at || check.assigned_at);
          const now = new Date();
          
          if (check.completed_at) {
            const completedDate = new Date(check.completed_at);
            daysElapsed = Math.ceil((completedDate - assignedDate) / (1000 * 60 * 60 * 24));
          } else {
            daysElapsed = Math.ceil((now - assignedDate) / (1000 * 60 * 60 * 24));
          }
          
          isWithinTat = daysElapsed <= agentTat;
        }
        
        return { ...check, client_tat: clientTat, agent_tat: agentTat, days_elapsed: daysElapsed, is_within_tat: isWithinTat };
      });
      
      setChecks(checksData);
      setClients(clientsRes.data || []);
      setVendors((vendorsRes.data || []).filter(v => v.onboarding_status !== 'suspended' && v.is_active !== false).sort((a, b) => (a.vendor_name || a.name || '').localeCompare(b.vendor_name || b.name || '')));
      setFieldAgents(fieldAgentsRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [checkTypeTAT]);

  useEffect(() => {
    fetchCheckTypeTAT();
  }, [fetchCheckTypeTAT]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch vendor team agents for transfer dialog
  useEffect(() => {
    const fetchVendorTeamAgents = async () => {
      try {
        const res = await axios.get('/agent/vendor-team-agents');
        setVendorTeamAgents(res.data || []);
      } catch (err) { console.error('Failed to fetch vendor team agents:', err); }
    };
    fetchVendorTeamAgents();
  }, []);

  // Computed values - for vendor agents, include checks assigned to their vendor company
  const myChecks = useMemo(() => {
    const filtered = checks.filter(c => {
      // Direct assignment to user by ID or email (current OR original before vendor overwrite)
      if (c.assigned_to === user?.id || c.assigned_agent_id === user?.id || c.agent_assigned_to === user?.id) return true;
      if (user?.email && (c.assigned_to_email === user.email || c.agent_assigned_to_email === user.email)) return true;
      // For vendor agents, also include checks assigned to their vendor company
      if (user?.vendor_id) {
        // Prioritize assigned_vendor.vendor_id over assigned_to (assigned_to may be an agent ID)
        const checkVendorId = c.assigned_vendor?.vendor_id || c.vendor_id;
        if (checkVendorId === user.vendor_id) return true;
      }
      // For vendor_management role without vendor_id, show ALL vendor-assigned checks + directly assigned
      if (user?.role === 'vendor_management' && !user?.vendor_id) {
        // Check if this check has been assigned to any vendor
        if (c.assigned_vendor?.vendor_id || c.assignment_type === 'vendor') return true;
        // Also check if assigned_to_name matches current user
        if (c.assigned_to_name === user?.name || c.agent_assigned_to_name === user?.name) return true;
      }
      return false;
    });
    
    // Sort by most recent first (assigned_at or vendor_assigned_at or created_at)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.vendor_assigned_at || a.assigned_at || a.created_at || 0);
      const dateB = new Date(b.vendor_assigned_at || b.assigned_at || b.created_at || 0);
      return dateB - dateA; // Most recent first
    });
  }, [checks, user]);
  
  // Get unique parent checks for stats (count main checks, not expanded subchecks)
  const uniqueParentChecks = useMemo(() => {
    const parentMap = new Map();
    myChecks.forEach(c => {
      const parentId = c.parent_check_id || c.check_id;
      if (!parentMap.has(parentId)) {
        // For parent check status: completed only if ALL subchecks are completed
        const siblingSubchecks = myChecks.filter(sc => (sc.parent_check_id || sc.check_id) === parentId);
        const COMPLETED_STATUSES = ['verified', 'completed', 'clear', 'report_approved', 'qc_approved'];
        const allCompleted = siblingSubchecks.every(sc => COMPLETED_STATUSES.includes(sc.status) || sc.verification_progress === 'completed');
        const anyInProgress = siblingSubchecks.some(sc => sc.verification_progress === 'in_progress' || sc.status === 'in_progress');
        const anyInsufficiency = siblingSubchecks.some(sc => sc.status === 'insufficiency' || sc.verification_progress === 'insufficiency');
        
        parentMap.set(parentId, {
          ...c,
          parent_check_id: parentId,
          is_completed: allCompleted,
          is_in_progress: anyInProgress && !allCompleted,
          has_insufficiency: anyInsufficiency,
          subcheck_count: siblingSubchecks.length,
          completed_subcheck_count: siblingSubchecks.filter(sc => COMPLETED_STATUSES.includes(sc.status) || sc.verification_progress === 'completed').length
        });
      }
    });
    return Array.from(parentMap.values());
  }, [myChecks]);
  
  // Stats based on unique parent checks
  const completedChecks = useMemo(() => uniqueParentChecks.filter(c => c.is_completed), [uniqueParentChecks]);

  // Apply date filter to completed checks for display.
  // Vendor filter applied independently from the Dashboard's vendor
  // filter (May 24, 2026 — users want the Completed tab to scope by
  // any vendor regardless of dashboard state).
  const filteredCompletedChecks = useMemo(() => {
    // First — apply the tab-local Vendor filter.
    let list = completedChecks;
    if (completedVendorFilter && completedVendorFilter !== 'inherit' && completedVendorFilter !== 'all') {
      list = list.filter(c => {
        const vid = c.assigned_vendor?.vendor_id || c.vendor_id;
        return vid === completedVendorFilter;
      });
    } else if (completedVendorFilter === 'inherit' && filters.vendorId && filters.vendorId !== 'all') {
      // Default behaviour — inherit the Dashboard's filter.
      list = list.filter(c => {
        const vid = c.assigned_vendor?.vendor_id || c.vendor_id;
        return vid === filters.vendorId;
      });
    }
    if (!exportDateFilter || exportDateFilter === 'latest') return list;
    return list.filter(check => {
      const checkDate = new Date(check.completed_at || check.verified_at || check.updated_at || check.created_at);
      if (isNaN(checkDate.getTime())) return true;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (exportDateFilter === 'today') {
        return checkDate >= today;
      } else if (exportDateFilter === 'weekly') {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return checkDate >= weekStart;
      } else if (exportDateFilter === 'last_month') {
        // Last calendar month — 1st of previous month to last day of
        // previous month inclusive (May 24, 2026).
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        return checkDate >= lastMonthStart && checkDate <= lastMonthEnd;
      } else if (exportDateFilter === 'between' && exportDateFrom && exportDateTo) {
        const from = new Date(exportDateFrom);
        const to = new Date(exportDateTo + 'T23:59:59');
        return checkDate >= from && checkDate <= to;
      }
      return true;
    });
  }, [completedChecks, exportDateFilter, exportDateFrom, exportDateTo, completedVendorFilter, filters.vendorId]);
  const pendingChecks = useMemo(() => uniqueParentChecks.filter(c => !c.is_completed), [uniqueParentChecks]);
  const notInitiatedChecks = useMemo(() => uniqueParentChecks.filter(c => !c.verification_progress || c.verification_progress === 'not_initiated'), [uniqueParentChecks]);

  // Unique agents derived from checks (for external vendor agent filter)
  const uniqueAgents = useMemo(() => {
    const agentMap = new Map();
    myChecks.forEach(c => {
      const agentId = c.assigned_agent_id || c.assigned_to_agent_id || c.assigned_to;
      const agentName = c.assigned_to_name || c.assigned_agent_name || '';
      if (agentId && agentName && agentId !== c.assigned_vendor?.vendor_id) {
        agentMap.set(agentId, { id: agentId, name: agentName });
      }
    });
    return Array.from(agentMap.values());
  }, [myChecks]);
  // Not Sent to Vendor - checks without vendor_name that need vendor processing
  const notSentToVendorChecks = useMemo(() => uniqueParentChecks.filter(c => 
    !c.vendor_name && (!c.verification_progress || c.verification_progress === 'not_initiated') && !c.is_completed
  ), [uniqueParentChecks]);
  const withinTatChecks = useMemo(() => uniqueParentChecks.filter(c => c.is_within_tat && !c.is_completed), [uniqueParentChecks]);
  const overTatChecks = useMemo(() => uniqueParentChecks.filter(c => !c.is_within_tat && !c.is_completed), [uniqueParentChecks]);
  const newlyAddedChecks = useMemo(() => uniqueParentChecks.filter(c => {
    if (!c.assigned_at && !c.created_at) return false;
    const addedDate = new Date(c.assigned_at || c.created_at);
    const hoursDiff = (new Date() - addedDate) / (1000 * 60 * 60);
    return hoursDiff <= 24;
  }), [uniqueParentChecks]);
  
  // Insufficiency count - use open insufficiency records as the single source of truth
  const [insufficiencies, setInsufficiencies] = useState([]);
  const insuffCount = useMemo(() => {
    const openInsuff = insufficiencies.filter(i => i.status === 'open').length;
    // Fall back to check flags only if no insufficiency records loaded yet
    if (insufficiencies.length === 0) {
      return uniqueParentChecks.filter(c => c.has_insufficiency).length;
    }
    return openInsuff;
  }, [uniqueParentChecks, insufficiencies]);

  // Fetch insufficiencies
  useEffect(() => {
    const fetchInsuff = async () => {
      try {
        const res = await axios.get('/insufficiencies');
        setInsufficiencies(res.data || []);
      } catch (error) {
        console.log('No insufficiencies endpoint');
      }
    };
    fetchInsuff();
  }, []);

  // Client TAT Summary
  const uniqueStates = useMemo(() => {
    const states = new Set();
    checks.forEach(c => {
      const st = c.state || c.subcheck_data?.state || '';
      if (st) states.add(st);
    });
    return Array.from(states).sort();
  }, [checks]);

  const clientTatSummary = useMemo(() => {
    const summary = {};
    myChecks.forEach(check => {
      const clientName = check.client_name || 'Unknown Client';
      if (!summary[clientName]) {
        summary[clientName] = { total: 0, within_tat: 0, over_tat: 0, check_types: {} };
      }
      summary[clientName].total++;
      if (check.is_within_tat) summary[clientName].within_tat++;
      else summary[clientName].over_tat++;
      
      const checkType = check.check_type || 'other';
      if (!summary[clientName].check_types[checkType]) {
        summary[clientName].check_types[checkType] = { total: 0, within_tat: 0 };
      }
      summary[clientName].check_types[checkType].total++;
      if (check.is_within_tat) summary[clientName].check_types[checkType].within_tat++;
    });
    return summary;
  }, [myChecks]);

  const getCheckTypeLabel = (checkType) => {
    const typeStr = getCheckTypeString(checkType);
    const found = CHECK_TYPES.find(ct => ct.value === typeStr);
    if (found) return found.label;
    return typeStr?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Other';
  };

  // Generate formatted text for field agent (copy to WhatsApp/SMS)
  const generateFieldAgentText = (check, checkDetails = null) => {
    const checkType = check.check_type?.toLowerCase() || '';
    const baseUrl = window.location.origin;
    const formLink = `${baseUrl}/field-verification/${check.case_id}/${check.check_id || check.id}`;
    
    let text = '';
    
    if (checkType.includes('address')) {
      const addressType = check.address_type || checkDetails?.address_type || check.subcheck_type || 'Present';
      const sd = check.subcheck_data || {};
      const addr = check.address || check.verification_address || sd.complete_address || sd.address_line1 || '-';
      const addrLine2 = sd.address_line2 ? `, ${sd.address_line2}` : '';
      const fullAddr = addr !== '-' && addrLine2 ? `${addr}${addrLine2}` : addr;
      text = `━━━━━━━━━━━━━━━━━━━━
ADDRESS VERIFICATION
━━━━━━━━━━━━━━━━━━━━
CHECK ID: ${check.case_id || '-'}
SUB-CHECK ID: ${check.check_id || check.id || '-'}
JOINING COMPANY: ${check.client_name || '-'}
ADDRESS TYPE: ${addressType}
EMPLOYEE: ${check.employee_name || '-'}
FATHER NAME: ${check.father_name || checkDetails?.father_name || '-'}
ADDRESS: ${fullAddr}
CITY: ${check.city || checkDetails?.city || sd.city || '-'}
LANDMARK: ${check.landmark || checkDetails?.landmark || sd.landmark || '-'}
STATE: ${check.state || checkDetails?.state || sd.state || '-'}
PINCODE: ${check.pincode || checkDetails?.pincode || sd.pincode || '-'}
CONTACT: ${check.phone || checkDetails?.phone || '-'}
ALT CONTACT: ${check.alternate_contact || checkDetails?.alternate_contact || '-'}
REMARKS: ${check.special_instructions || check.remarks || checkDetails?.remarks || '-'}

📋 VERIFICATION FORM:
${formLink}`;
    } else if (checkType.includes('employment')) {
      const employmentType = check.employment_type || checkDetails?.employment_type || 'Current';
      text = `━━━━━━━━━━━━━━━━━━━━
EMPLOYMENT VERIFICATION
━━━━━━━━━━━━━━━━━━━━
CHECK ID: ${check.case_id || '-'}
SUB-CHECK ID: ${check.check_id || check.id || '-'}
EMPLOYMENT TYPE: ${employmentType}
JOINING COMPANY: ${check.client_name || '-'}
EMPLOYEE: ${check.employee_name || '-'}
FATHER NAME: ${check.father_name || checkDetails?.father_name || '-'}
COMPANY TO VISIT: ${check.company_name || checkDetails?.company_name || '-'}
ADDRESS: ${check.company_address || checkDetails?.company_address || check.address || '-'}
CITY: ${check.company_city || checkDetails?.company_city || check.city || '-'}
LANDMARK: ${check.company_landmark || checkDetails?.landmark || '-'}
STATE: ${check.company_state || checkDetails?.company_state || check.state || '-'}
PINCODE: ${check.company_pincode || checkDetails?.company_pincode || check.pincode || '-'}
CONTACT: ${check.hr_contact || checkDetails?.hr_contact || check.phone || '-'}
ALT CONTACT: ${check.alternate_contact || '-'}
REMARKS: ${check.special_instructions || check.remarks || checkDetails?.remarks || '-'}

📋 VERIFICATION FORM:
${formLink}`;
    } else if (checkType.includes('education')) {
      const educationType = check.education_type || checkDetails?.education_type || 'Highest';
      text = `━━━━━━━━━━━━━━━━━━━━
EDUCATION VERIFICATION
━━━━━━━━━━━━━━━━━━━━
CHECK ID: ${check.case_id || '-'}
SUB-CHECK ID: ${check.check_id || check.id || '-'}
EDUCATION TYPE: ${educationType}
JOINING COMPANY: ${check.client_name || '-'}
EMPLOYEE: ${check.employee_name || '-'}
FATHER NAME: ${check.father_name || checkDetails?.father_name || '-'}
INSTITUTION: ${check.institution_name || checkDetails?.institution_name || '-'}
UNIVERSITY: ${check.university_name || checkDetails?.university_name || '-'}
ADDRESS: ${check.institution_address || checkDetails?.institution_address || check.address || '-'}
CITY: ${check.institution_city || checkDetails?.institution_city || check.city || '-'}
LANDMARK: ${check.institution_landmark || checkDetails?.landmark || '-'}
STATE: ${check.institution_state || checkDetails?.institution_state || check.state || '-'}
PINCODE: ${check.institution_pincode || checkDetails?.institution_pincode || check.pincode || '-'}
CONTACT: ${check.registrar_contact || checkDetails?.registrar_contact || check.phone || '-'}
ALT CONTACT: ${check.alternate_contact || '-'}
REMARKS: ${check.special_instructions || check.remarks || checkDetails?.remarks || '-'}

📋 VERIFICATION FORM:
${formLink}`;
    } else {
      // Generic format for other check types (criminal, court, ID, etc.)
      text = `━━━━━━━━━━━━━━━━━━━━
FIELD VERIFICATION
━━━━━━━━━━━━━━━━━━━━
CHECK ID: ${check.case_id || '-'}
SUB-CHECK ID: ${check.check_id || check.id || '-'}
CHECK TYPE: ${check.check_type?.replace(/_/g, ' ').toUpperCase() || '-'}
JOINING COMPANY: ${check.client_name || '-'}
EMPLOYEE: ${check.employee_name || '-'}
FATHER NAME: ${check.father_name || checkDetails?.father_name || '-'}
ADDRESS: ${check.address || check.verification_address || '-'}
CITY: ${check.city || checkDetails?.city || '-'}
STATE: ${check.state || checkDetails?.state || '-'}
PINCODE: ${check.pincode || checkDetails?.pincode || '-'}
CONTACT: ${check.phone || checkDetails?.phone || '-'}
ALT CONTACT: ${check.alternate_contact || '-'}
REMARKS: ${check.special_instructions || check.remarks || checkDetails?.remarks || '-'}

📋 VERIFICATION FORM:
${formLink}`;
    }
    
    return text;
  };

  // Safe clipboard copy with fallback
  const safeClipboardWrite = async (text) => {
    // Method 1: Modern Clipboard API
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      console.warn('Clipboard API failed:', e);
    }
    // Method 2: execCommand fallback
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) return true;
    } catch (e) {
      console.warn('execCommand copy failed:', e);
    }
    return false;
  };

  // Copy check details for field agent (WhatsApp/SMS)
  const copyForFieldAgent = async (check) => {
    try {
      // Try to fetch full check details for more information
      const checkId = check.check_id || check.id;
      const res = await axios.get(`/cases/${check.case_id}/checks/${checkId}/details`).catch(() => null);
      const checkDetails = res?.data || null;
      
      const text = generateFieldAgentText(check, checkDetails);
      const copied = await safeClipboardWrite(text);
      if (copied) {
        toast.success('Copied! Share via WhatsApp or SMS');
      } else {
        toast.error('Failed to copy to clipboard');
      }
    } catch (error) {
      // Use basic info if API fails
      const text = generateFieldAgentText(check);
      const copied = await safeClipboardWrite(text);
      if (copied) {
        toast.success('Copied! Share via WhatsApp or SMS');
      } else {
        toast.error('Failed to copy to clipboard');
      }
    }
  };

  // Generate field verification form link
  const generateFieldLink = (check) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/field-verification/${check.case_id}/${check.check_id || check.id}`;
  };

  // Open Field Agent assignment dialog
  const openAssignFieldAgent = (check) => {
    setSelectedCheckForFA(check);
    setFieldAgentForm({ 
      name: check.field_agent_name || '', 
      phone: check.field_agent_phone || '', 
      pincode: check.pincode || check.address_pincode || '' 
    });
    setShowAssignFieldAgent(true);
  };

  // Assign/Create Field Agent and copy link
  const handleAssignFieldAgent = async () => {
    if (!fieldAgentForm.name.trim()) {
      toast.error('Please enter Field Agent name');
      return;
    }
    
    try {
      // Save or update field agent
      const agentRes = await axios.post('/vendor/field-agents', {
        name: fieldAgentForm.name.trim(),
        phone: fieldAgentForm.phone.trim(),
        pincode: fieldAgentForm.pincode.trim()
      });
      
      const fieldAgentId = agentRes.data?.id;
      
      // Assign field agent to check (this also sends SMS and returns check details)
      const assignRes = await axios.post(`/vendor/assign-field-agent/${selectedCheckForFA.case_id}/${selectedCheckForFA.check_id || selectedCheckForFA.id}`, {
        field_agent_id: fieldAgentId,
        field_agent_name: fieldAgentForm.name.trim(),
        field_agent_phone: fieldAgentForm.phone.trim(),
        send_sms: true
      });
      
      // Use check details from backend response for accurate clipboard text
      const checkDetails = assignRes.data?.check_details || {};
      const enrichedCheck = {
        ...selectedCheckForFA,
        address_type: checkDetails.address_type,
        address: checkDetails.address,
        city: checkDetails.city,
        state: checkDetails.state,
        pincode: checkDetails.pincode,
        landmark: checkDetails.landmark,
        phone: checkDetails.contact,
        father_name: checkDetails.father_name,
        employee_name: checkDetails.employee_name || selectedCheckForFA.employee_name,
        client_name: checkDetails.client_name || selectedCheckForFA.client_name
      };
      
      // Generate clipboard text with accurate details
      const text = generateFieldAgentText(enrichedCheck, checkDetails);
      
      // Try to copy to clipboard
      const clipboardSuccess = await safeClipboardWrite(text);
      
      // Always show the text in the dialog for manual copy/verification
      setCopiedText(text);
      if (clipboardSuccess) {
        toast.success('Field Agent assigned & details copied!', { duration: 5000 });
      } else {
        toast.success('Field Agent assigned! Copy the details below to share.', { duration: 5000 });
      }
      
      // Refresh data to show updated field agent name
      fetchData();
    } catch (error) {
      console.error('Failed to assign field agent:', error);
      toast.error('Failed to assign field agent');
    }
  };

  // Open Field Verification View (Form Update for vendors)
  const openFieldVerificationView = async (check) => {
    setSelectedCheck(check);
    setShowFieldVerificationView(true);
    
    // Fetch field verification data and photos
    try {
      const [detailsRes, photosRes] = await Promise.all([
        axios.get(`/cases/${check.case_id}/checks/${check.check_id || check.id}/details`).catch(() => ({ data: {} })),
        axios.get(`/cases/${check.case_id}/checks/${check.check_id || check.id}/annexures`).catch(() => ({ data: [] }))
      ]);
      
      setFieldVerificationData({
        ...check,
        ...detailsRes.data,
        field_verification: check.field_verification || detailsRes.data?.field_verification || {}
      });
      setFieldVerificationPhotos(photosRes.data || []);
    } catch (error) {
      console.error('Failed to load field verification data:', error);
      setFieldVerificationData(check);
      setFieldVerificationPhotos([]);
    }
  };

  const getFilteredChecks = (checksToFilter) => {
    return checksToFilter.filter(check => {
      // Date filter
      if (filters.dateFilterType && filters.dateFilterType !== 'latest') {
        const checkDate = new Date(check.assigned_at || check.created_at || check.vendor_assigned_at);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        if (filters.dateFilterType === 'today') {
          if (checkDate < today) return false;
        } else if (filters.dateFilterType === 'this_week') {
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          if (checkDate < weekStart) return false;
        } else if (filters.dateFilterType === 'this_month') {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          if (checkDate < monthStart) return false;
        }
      }
      // Client filter
      if (filters.clientId && filters.clientId !== 'all') {
        if (check.client_id !== filters.clientId) return false;
      }
      // Check type filter - compare normalized check type strings
      if (filters.checkType && filters.checkType !== 'all') {
        const checkTypeStr = getCheckTypeString(check.check_type);
        if (checkTypeStr !== filters.checkType) return false;
      }
      // Status filter
      if (filters.status && filters.status !== 'all') {
        const verificationProgress = check.verification_progress || check.status || '';
        if (filters.status === 'not_initiated' && verificationProgress && verificationProgress !== 'not_initiated' && verificationProgress !== 'pending') return false;
        if (filters.status === 'in_progress' && verificationProgress !== 'in_progress') return false;
        if (filters.status === 'completed' && !['completed', 'verified', 'clear'].includes(verificationProgress)) return false;
        if (filters.status === 'insufficiency' && verificationProgress !== 'insufficiency' && !check.has_insufficiency) return false;
      }
      // TAT filter
      if (filters.tatStatus && filters.tatStatus !== 'all') {
        if (filters.tatStatus === 'within_tat' && !check.is_within_tat) return false;
        if (filters.tatStatus === 'over_tat' && check.is_within_tat) return false;
      }
      // Vendor filter
      if (filters.vendorId && filters.vendorId !== 'all') {
        const checkVendorId = check.vendor_id || check.assigned_vendor_id || check.assigned_vendor?.vendor_id || check.assigned_vendor?.id;
        if (checkVendorId !== filters.vendorId) return false;
      }
      // Agent filter (for external vendors to filter by field agent)
      if (filters.agentId && filters.agentId !== 'all') {
        const checkFieldAgentId = check.field_agent_id || check.field_verification?.agent_id || '';
        if (checkFieldAgentId !== filters.agentId) return false;
      }
      // Field Status filter
      if (filters.fieldStatus && filters.fieldStatus !== 'all') {
        const fvStatus = check.field_verification?.status || check.field_verification_status || '';
        const isSubmitted = check.field_verification_submitted || check.vendor_report_uploaded;
        if (filters.fieldStatus === 'submitted' && !isSubmitted) return false;
        if (filters.fieldStatus === 'pending' && (isSubmitted || (fvStatus !== 'pending' && fvStatus !== '' && fvStatus !== 'not_started'))) return false;
        if (filters.fieldStatus === 'in_progress' && fvStatus !== 'in_progress') return false;
        if (filters.fieldStatus === 'completed' && fvStatus !== 'completed' && fvStatus !== 'verified') return false;
        if (filters.fieldStatus === 'not_assigned' && fvStatus !== '' && fvStatus !== 'not_start' && check.field_agent_name) return false;
      }
      // State filter
      if (filters.state && filters.state !== 'all') {
        const checkState = (check.state || check.subcheck_data?.state || '').toLowerCase();
        if (checkState !== filters.state.toLowerCase()) return false;
      }
      // Education Type filter
      if (filters.educationType && filters.educationType !== 'all') {
        const checkType = getCheckTypeString(check.check_type);
        if (checkType !== 'education_check') return false;
        const eduType = (check.subcheck_type || check.subcheck_data?.education_type || check.subcheck_data?.type || '').toLowerCase();
        const filterVal = filters.educationType.toLowerCase();
        if (!eduType.includes(filterVal)) return false;
      }
      // Search filter - includes Check ID, Case ID, Field Agent Name, Pincode, State, City, Education Type
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const fieldAgentName = check.field_agent_name || check.field_agent?.name || '';
        const pincode = check.pincode || check.address_pincode || check.verification_pincode || '';
        const state = check.state || check.subcheck_data?.state || '';
        const city = check.city || check.subcheck_data?.city || '';
        const eduType = check.subcheck_type || check.subcheck_data?.education_type || '';
        if (!check.employee_name?.toLowerCase().includes(searchLower) &&
            !check.case_id?.toLowerCase().includes(searchLower) &&
            !check.client_name?.toLowerCase().includes(searchLower) &&
            !check.check_id?.toLowerCase().includes(searchLower) &&
            !fieldAgentName.toLowerCase().includes(searchLower) &&
            !state.toLowerCase().includes(searchLower) &&
            !city.toLowerCase().includes(searchLower) &&
            !eduType.toLowerCase().includes(searchLower) &&
            !pincode.includes(searchLower)) return false;
      }
      return true;
    });
  };

  const getPaginatedChecks = (filteredChecks) => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredChecks.slice(startIndex, startIndex + pageSize);
  };

  // Multiselect handlers
  const toggleCheckSelection = (checkId) => {
    setSelectedChecks(prev => 
      prev.includes(checkId) 
        ? prev.filter(id => id !== checkId) 
        : [...prev, checkId]
    );
  };

  const toggleSelectAll = (checksToSelect) => {
    const allIds = checksToSelect.map(c => c.check_id || c.id);
    const allSelected = allIds.every(id => selectedChecks.includes(id));
    if (allSelected) {
      setSelectedChecks(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedChecks(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const getNotInitiatedChecks = (checksArr) => {
    return checksArr.filter(c => !c.verification_progress || c.verification_progress === 'not_initiated');
  };

  // Auto-assign field agents based on pincode matching
  const handleAutoAssignFieldAgents = async (checkIds) => {
    try {
      const res = await axios.post('/vendor/field-agents/auto-assign', { check_ids: checkIds });
      return res.data;
    } catch (error) {
      console.error('Auto-assign failed:', error);
      return { assignments: [], unassigned: [], matched: 0, unmatched: checkIds.length };
    }
  };

  const handleBulkInitiate = async () => {
    if (selectedChecks.length === 0) {
      toast.error('No checks selected');
      return;
    }
    // Only require vendor selection if NOT auto-assign mode AND user is NOT an external vendor
    if (!bulkInitiateAutoAssign && !bulkInitiateVendorId && user?.role !== 'external_vendor') {
      toast.error('Please select a vendor to assign checks to');
      return;
    }
    // Validate field agent if manual section is shown
    if (showBulkFieldAgentSection && !bulkFieldAgentForm.name.trim()) {
      toast.error('Please enter Field Agent name');
      return;
    }
    
    setBulkInitiateLoading(true);
    try {
      const results = { success: 0, failed: 0, autoAssigned: 0 };
      let manualFieldAgentId = null;
      
      // If auto-assign mode is ON, try to match field agents by pincode
      let autoAssignResults = null;
      if (bulkInitiateAutoAssign && !showBulkFieldAgentSection) {
        autoAssignResults = await handleAutoAssignFieldAgents(selectedChecks);
        console.log('Auto-assign results:', autoAssignResults);
      }
      
      // Create manual field agent if provided
      if (showBulkFieldAgentSection && bulkFieldAgentForm.name.trim()) {
        try {
          const agentRes = await axios.post('/vendor/field-agents', {
            name: bulkFieldAgentForm.name.trim(),
            phone: bulkFieldAgentForm.phone.trim(),
            pincode: bulkFieldAgentForm.pincode.trim()
          });
          manualFieldAgentId = agentRes.data?.id;
        } catch (error) {
          console.error('Failed to create field agent:', error);
        }
      }
      
      for (const checkId of selectedChecks) {
        try {
          // Find the check to get case_id
          const check = checks.find(c => (c.check_id || c.id) === checkId);
          if (!check) {
            results.failed++;
            continue;
          }
          
          if (bulkInitiateAutoAssign && !showBulkFieldAgentSection) {
            // Auto-assign mode - initiate and assign FE by pincode if matched
            await axios.post(`/checks/${checkId}/initiate`, {
              initiated_by: user?.id,
              initiated_at: new Date().toISOString(),
              special_instructions: bulkSpecialInstructions,
              remarks: bulkSpecialInstructions
            });
            
            // Check if this check has an auto-assigned field agent
            const autoMatch = autoAssignResults?.assignments?.find(a => a.check_id === checkId);
            if (autoMatch) {
              try {
                await axios.post(`/vendor/assign-field-agent/${check.case_id}/${checkId}`, {
                  field_agent_id: autoMatch.field_agent.id,
                  field_agent_name: autoMatch.field_agent.name,
                  field_agent_phone: autoMatch.field_agent.phone || '',
                  special_instructions: bulkSpecialInstructions
                });
                results.autoAssigned++;
              } catch (error) {
                console.error(`Failed to auto-assign FE to check ${checkId}:`, error);
              }
            }
          } else if (!bulkInitiateAutoAssign && bulkInitiateVendorId) {
            // Manual assign mode - assign to selected vendor (skip individual email)
            await axios.post(`/cases/${check.case_id}/checks/${checkId}/assign`, {
              vendor_id: bulkInitiateVendorId,
              assigned_by: user?.id,
              assignment_type: 'vendor',
              special_instructions: bulkSpecialInstructions,
              remarks: bulkSpecialInstructions,
              skip_email: true
            });
          } else if (showBulkFieldAgentSection && !bulkInitiateVendorId) {
            // Field agent assignment only (no vendor re-assignment needed)
            // Checks are already assigned to this vendor, just need field agent
            // Field agent assignment happens below in the common block
          } else {
            // Just initiate without auto-assign (when manual FE section is shown)
            await axios.post(`/checks/${checkId}/initiate`, {
              initiated_by: user?.id,
              initiated_at: new Date().toISOString()
            });
          }
          
          // Assign manual field agent if provided (overrides auto-assign)
          if (manualFieldAgentId && bulkFieldAgentForm.name.trim()) {
            try {
              await axios.post(`/vendor/assign-field-agent/${check.case_id}/${checkId}`, {
                field_agent_id: manualFieldAgentId,
                field_agent_name: bulkFieldAgentForm.name.trim(),
                field_agent_phone: bulkFieldAgentForm.phone.trim(),
                special_instructions: bulkSpecialInstructions
              });
            } catch (error) {
              console.error(`Failed to assign field agent to check ${checkId}:`, error);
            }
          }
          
          results.success++;
        } catch (error) {
          console.error(`Failed to process check ${checkId}:`, error);
          results.failed++;
        }
      }
      
      // Copy detailed verification text for each check (like individual "Copy for Field Agent")
      if (showBulkFieldAgentSection && bulkFieldAgentForm.name.trim() && results.success > 0) {
        const detailedTexts = [];
        for (const checkId of selectedChecks) {
          const check = checks.find(c => (c.check_id || c.id) === checkId);
          if (check) {
            try {
              const res = await axios.get(`/cases/${check.case_id}/checks/${checkId}/details`).catch(() => null);
              const checkDetails = res?.data || null;
              detailedTexts.push(generateFieldAgentText(check, checkDetails));
            } catch {
              detailedTexts.push(generateFieldAgentText(check));
            }
          }
        }
        
        const fullText = detailedTexts.join('\n\n');
        const copied = await safeClipboardWrite(fullText);
        if (copied) {
          toast.success(`${results.success} checks processed & detailed verification info copied!`);
        } else {
          toast.success(`Bulk processing complete: ${results.success} succeeded, ${results.failed} failed`);
        }
      } else if (results.autoAssigned > 0) {
        toast.success(`${results.success} checks initiated, ${results.autoAssigned} auto-assigned to Field Agents by pincode`);
      } else {
        toast.success(`Bulk ${bulkInitiateAutoAssign ? 'initiation' : 'assignment'} complete: ${results.success} succeeded, ${results.failed} failed`);
      }
      
      // Send consolidated assignment email to vendor after all assignments
      if (!bulkInitiateAutoAssign && bulkInitiateVendorId && results.success > 0) {
        try {
          const successfulAssignments = selectedChecks
            .map(checkId => {
              const check = checks.find(c => (c.check_id || c.id) === checkId);
              return check ? { case_id: check.case_id, check_id: checkId } : null;
            })
            .filter(Boolean);
          
          await axios.post('/vendor/send-consolidated-assignment-email', {
            vendor_id: bulkInitiateVendorId,
            check_assignments: successfulAssignments,
            special_instructions: bulkSpecialInstructions
          });
        } catch (emailErr) {
          console.error('Failed to send consolidated vendor email:', emailErr);
          toast.error('Assignments done but email notification failed');
        }
      }
      
      setSelectedChecks([]);
      setShowBulkInitiateDialog(false);
      setBulkInitiateVendorId('');
      setBulkFieldAgentForm({ name: '', phone: '', pincode: '' });
      setShowBulkFieldAgentSection(false);
      fetchData();
    } catch (error) {
      toast.error('Bulk operation failed');
    } finally {
      setBulkInitiateLoading(false);
    }
  };

  // Manual Urgent TAT Reminder (May 22, 2026) — calls
  // /api/vendor/send-urgent-tat-reminder. Server groups by vendor and
  // sends one email per vendor with all selected rows + red banner.
  const sendUrgentReminder = async (singleCheck = null) => {
    // Build the [{case_id, check_id}] payload from either a per-row call
    // or the current multi-select. We look up case_id from `myChecks`
    // (the source of `Recent Assigned Checks`) since selectedChecks just
    // stores check_id strings.
    const targets = singleCheck
      ? [{ case_id: singleCheck.case_id, check_id: singleCheck.check_id }]
      : selectedChecks
          .map((cid) => {
            const row = (myChecks || []).find((rc) => rc.check_id === cid);
            return row ? { case_id: row.case_id, check_id: row.check_id } : null;
          })
          .filter(Boolean);
    if (targets.length === 0) {
      toast.error('No checks selected');
      return;
    }
    setUrgentReminderSending(true);
    setUrgentReminderResult(null);
    try {
      const res = await axios.post('/vendor/send-urgent-tat-reminder', { check_assignments: targets });
      setUrgentReminderResult(res.data);
      toast.success(res.data.message || 'Urgent reminder sent');
      if (!singleCheck) setSelectedChecks([]);
      setTimeout(() => {
        setShowUrgentReminderDialog(false);
        setUrgentReminderSingleCheck(null);
        setUrgentReminderResult(null);
      }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send urgent reminder');
    } finally {
      setUrgentReminderSending(false);
    }
  };

  // Transfer selected checks to vendor team agent
  const handleTransferToVendor = async () => {
    if (selectedChecks.length === 0) {
      toast.error('No checks selected');
      return;
    }
    if (!transferAgentId) {
      toast.error('Please select a vendor team agent');
      return;
    }
    setTransferLoading(true);
    try {
      const results = { success: 0, failed: 0 };
      
      for (const checkId of selectedChecks) {
        const check = checks.find(c => (c.check_id || c.id) === checkId);
        if (!check) { results.failed++; continue; }
        try {
          await axios.post(`/agent/checks/${check.case_id}/${checkId}/transfer-to-agent`, {
            agent_id: transferAgentId,
            reason: transferReason || 'Transferred to vendor team agent',
          });
          results.success++;
        } catch (err) {
          console.error(`Failed to transfer check ${checkId}:`, err);
          results.failed++;
        }
      }

      toast.success(`Transfer complete: ${results.success} transferred${results.failed > 0 ? `, ${results.failed} failed` : ''}`);
      setSelectedChecks([]);
      setShowTransferDialog(false);
      setTransferAgentId('');
      setTransferReason('');
      fetchData();
    } catch (error) {
      toast.error('Transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };

  // Export completed checks — uses the SAME filtered list shown on
  // screen (filteredCompletedChecks), generates a multi-sheet
  // .xlsx workbook with one sheet per check type, and includes
  // Address / State / Pin Code / Vendor Cost columns. Dates are
  // formatted DD-MM-YYYY (no time). May 30, 2026.
  const handleExportCompletedChecks = async () => {
    setExportLoading(true);
    try {
      const dataToExport = filteredCompletedChecks;

      if (!dataToExport || dataToExport.length === 0) {
        toast.info('No completed checks found for the selected filters');
        setExportLoading(false);
        return;
      }

      // DD-MM-YYYY formatter — no time component
      const fmtDate = (val) => {
        if (!val) return '';
        const d = new Date(val);
        if (isNaN(d.getTime())) return '';
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
      };

      // Pretty label for check_type → sheet name (Excel limits sheet
      // names to 31 chars and forbids / \ ? * [ ])
      const CHECK_TYPE_LABEL = {
        address_check: 'Address',
        employment_check: 'Employment',
        education_check: 'Education',
        criminal_court_check: 'Criminal Court',
        criminal_advocate_check: 'Criminal Advocate',
        court_advocate_check: 'Criminal Advocate',
        id_check: 'ID Verification',
        reference_check: 'Reference',
        personal_reference: 'Personal Reference',
        professional_reference: 'Professional Reference',
        credit_check: 'Credit',
        drug_test: 'Drug Test',
        global_database: 'Global Database',
        world_criminal_db: 'World Database',
        uan_check: 'UAN',
      };
      const sheetLabel = (ct) => {
        const lbl = CHECK_TYPE_LABEL[ct] || (ct || 'Other')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase());
        // Sanitize for Excel
        return lbl.replace(/[\\/?*[\]:]/g, '').slice(0, 31) || 'Other';
      };

      const buildRow = (check) => {
        const vendorName = check.vendor_name
          || check.assigned_vendor?.vendor_name
          || check.assigned_vendor_name
          || '';
        const vendorCost = check.vendor_cost ?? 0;
        const extraCost = check.extra_cost ?? 0;
        const total = (Number(vendorCost) || 0) + (Number(extraCost) || 0);
        return {
          'Case ID': check.case_id || '',
          'Check ID': check.check_id || check.id || '',
          'Employee Name': check.employee_name || check.candidate_name || '',
          'Client Name': check.client_name || '',
          'Check Type': check.check_type || '',
          'Sub-Check Type': check.sub_option || check.subcheck_type || check.sub_check_type || '',
          'Status': check.status || '',
          'Address': check.resolved_full_address || '',
          'State': check.resolved_state || '',
          'Pin Code': check.resolved_pincode || '',
          'Assigned Date': fmtDate(check.assigned_at || check.vendor_assigned_at),
          'Completed Date': fmtDate(check.completed_at || check.verified_at),
          'TAT Days': check.days_elapsed ?? check.tat_days ?? '',
          'Vendor Name': vendorName,
          'Vendor Cost': Number(vendorCost) || 0,
          'Extra Cost': Number(extraCost) || 0,
          'Total': total,
          'Field Verification Status': check.field_verification_status || '',
          'Field Agent Remarks': check.field_agent_remarks || '',
          // May 30, 2026 — per user: these three now sit at the
          // very end of the row, after Field Agent Remarks.
          'Verification Result': check.verification_result || check.result || '',
          'Field Agent Name': check.field_agent_name || '',
          'Field Agent Phone': check.field_agent_phone || '',
        };
      };

      // Group by check_type → separate worksheets
      const grouped = {};
      dataToExport.forEach((check) => {
        const key = sheetLabel(check.check_type);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(buildRow(check));
      });

      const wb = XLSX.utils.book_new();

      // "All Checks" summary sheet first so a reviewer sees totals
      // up-front. Column order matches the per-type sheets.
      const allRows = dataToExport.map(buildRow);
      const wsAll = XLSX.utils.json_to_sheet(allRows);
      XLSX.utils.book_append_sheet(wb, wsAll, 'All Checks');

      // One sheet per check type, sorted alphabetically for stable
      // ordering across exports.
      Object.keys(grouped).sort().forEach((sheetName) => {
        const ws = XLSX.utils.json_to_sheet(grouped[sheetName]);
        // Ensure unique sheet name (defensive — sanitize already did this)
        let safe = sheetName;
        let i = 2;
        while (wb.SheetNames.includes(safe)) {
          safe = (sheetName + ' ' + i).slice(0, 31);
          i += 1;
        }
        XLSX.utils.book_append_sheet(wb, ws, safe);
      });

      const filename = `completed_checks_${exportDateFilter}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);

      toast.success(`Exported ${dataToExport.length} completed checks across ${Object.keys(grouped).length} sheet(s)`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  // Export vendor checks as multi-worksheet Excel file
  const [excelExportLoading, setExcelExportLoading] = useState(false);
  const handleExportVendorExcel = async () => {
    setExcelExportLoading(true);
    try {
      const response = await axios.get('/team/checks/export-excel', {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Vendor_Checks_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Excel file downloaded with separate worksheets for each check type');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error('Failed to export Excel file');
    } finally {
      setExcelExportLoading(false);
    }
  };

  // Fetch check details and documents
  const fetchCheckDetails = async (check) => {
    setLoadingDocs(true);
    try {
      // Fetch case details to get employee data
      const caseRes = await axios.get(`/cases/${check.case_id}`);
      setCheckDetailData(caseRes.data);
      
      // Fetch documents - use annexures endpoint + employee documents collection
      try {
        const docsRes = await axios.get(`/cases/${check.case_id}/checks/${check.check_id || check.id}/annexures`);
        const annexures = docsRes.data || [];
        const proofDocs = check.proof_documents || [];
        
        // Also fetch employee documents from documents collection
        let employeeDocs = [];
        const employeeId = caseRes.data?.employee?.id || check.employee_id;
        if (employeeId) {
          try {
            const empDocsRes = await axios.get(`/employees/${employeeId}/documents`);
            const allEmpDocs = empDocsRes.data || [];
            // Filter by check type relevance
            const ct = (check.check_type || '').toLowerCase();
            if (ct.includes('education')) {
              employeeDocs = allEmpDocs.filter(d => {
                const dtype = (d.document_type || d.doc_type || d.category || '').toLowerCase();
                return dtype.includes('education') || dtype.includes('degree') || dtype.includes('marks') || dtype.includes('certificate') || dtype.includes('diploma');
              });
            } else if (ct.includes('employment')) {
              employeeDocs = allEmpDocs.filter(d => {
                const dtype = (d.document_type || d.doc_type || d.category || '').toLowerCase();
                return dtype.includes('employment') || dtype.includes('relieving') || dtype.includes('payslip') || dtype.includes('offer') || dtype.includes('experience');
              });
            } else if (ct.includes('address')) {
              employeeDocs = allEmpDocs.filter(d => {
                const dtype = (d.document_type || d.doc_type || d.category || '').toLowerCase();
                return dtype.includes('address') || dtype.includes('aadhar') || dtype.includes('passport') || dtype.includes('utility');
              });
            } else if (ct.includes('id')) {
              employeeDocs = allEmpDocs.filter(d => {
                const dtype = (d.document_type || d.doc_type || d.category || '').toLowerCase();
                return dtype.includes('pan') || dtype.includes('aadhar') || dtype.includes('id') || dtype.includes('passport') || dtype.includes('voter');
              });
            }
          } catch (e) { /* ignore */ }
        }
        
        // Merge all sources, dedup by id
        const allDocs = [...annexures, ...proofDocs, ...employeeDocs];
        
        // Also add declaration_form from employee record if available
        const empData = caseRes.data?.employee;
        if (empData?.declaration_form_url) {
          const declExists = allDocs.some(d => d.document_type === 'declaration_form');
          if (!declExists) {
            allDocs.push({
              id: 'declaration_form_' + (empData.id || ''),
              document_type: 'declaration_form',
              original_filename: 'Candidate Declaration Form',
              file_url: empData.declaration_form_url,
              file_type: 'application/pdf'
            });
          }
        }
        
        const seen = new Set();
        const dedupedDocs = allDocs.filter(d => {
          const key = d.id || d.file_url || d.url;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setCheckDocuments(dedupedDocs);
      } catch (e) {
        setCheckDocuments(check.proof_documents || []);
      }
    } catch (error) {
      console.error('Failed to fetch check details:', error);
      setCheckDetailData(null);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Upload verification report for non-field checks (Criminal Court, Criminal Advocate, etc.)
  const handleUploadVerificationReport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCheck) return;
    setUploadingReport(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', `Verification Certificate - ${selectedCheck.check_id}`);
      const res = await axios.post(
        `/vendor/cases/${selectedCheck.case_id}/checks/${selectedCheck.check_id || selectedCheck.id}/upload-report`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Verification report uploaded successfully');
      // Refresh documents list
      await fetchCheckDetails(selectedCheck);
      // Update local check data to reflect vendor_report_uploaded
      setSelectedCheck(prev => ({ ...prev, vendor_report_uploaded: true }));
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload report');
    } finally {
      setUploadingReport(false);
      e.target.value = '';
    }
  };

  // Get subchecks based on check type
  const getSubchecks = (checkType, employeeData) => {
    if (!employeeData) return [];
    const subchecks = [];
    
    if (checkType === 'address_check' || checkType === 'address_verification') {
      const addresses = employeeData.addresses || [];
      addresses.forEach((addr, idx) => {
        subchecks.push({ type: addr.address_type || `Address ${idx + 1}`, ...addr });
      });
    } else if (checkType === 'employment_check') {
      const employers = employeeData.employment_history || [];
      employers.forEach((emp, idx) => {
        subchecks.push({ type: emp.company_name || `Employer ${idx + 1}`, ...emp });
      });
    } else if (checkType === 'education_check') {
      const education = employeeData.education || [];
      education.forEach((edu, idx) => {
        subchecks.push({ type: edu.university_name || edu.institution_name || `Education ${idx + 1}`, ...edu });
      });
    } else if (checkType === 'reference_check') {
      const refs = employeeData.references || [];
      refs.forEach((ref, idx) => {
        subchecks.push({ type: ref.name || `Reference ${idx + 1}`, ...ref });
      });
    } else if (checkType === 'id_check' || checkType === 'id_verification') {
      const ids = employeeData.id_documents || [];
      ids.forEach((id, idx) => {
        subchecks.push({ type: id.document_type || `ID ${idx + 1}`, ...id });
      });
    }
    return subchecks;
  };

  const openCheckDetails = async (check) => {
    setSelectedCheck(check);
    setCheckDetailData(null);
    setCheckDocuments([]);
    setPreviewDoc(null);
    setSelectedSubcheck(null);
    setShowCheckDetails(true);
    await fetchCheckDetails(check);
  };

  const openUpdateStatus = (check) => {
    setSelectedCheck(check);
    setShowUpdateStatus(true);
  };

  const openRaiseInsuff = (check) => {
    setSelectedCheck(check);
    setInsuffData({ reason: '', details: '' });
    setShowRaiseInsuff(true);
  };

  const handleRaiseInsuff = async () => {
    if (!selectedCheck || !insuffData.reason) {
      toast.error('Please select a reason');
      return;
    }
    try {
      await axios.post(`/insufficiencies`, {
        case_id: selectedCheck.case_id,
        check_id: selectedCheck.raw_check_id || selectedCheck.parent_check_id || selectedCheck.check_id || selectedCheck.id,
        check_type: selectedCheck.check_type,
        reason: insuffData.reason,
        details: insuffData.details,
        raised_by: user?.id,
        status: 'open'
      });
      toast.success('Insufficiency raised successfully');
      setShowRaiseInsuff(false);
      setSelectedCheck(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to raise insufficiency');
    }
  };

  const openReturnDialog = (check) => {
    setSelectedCheck(check);
    setReturnData({ reason: '', notes: '' });
    setShowReturnDialog(true);
  };

  const handleReturnCheck = async () => {
    if (!selectedCheck || !returnData.reason) {
      toast.error('Please select a reason');
      return;
    }
    try {
      await axios.post(`/agent/checks/${selectedCheck.case_id}/${selectedCheck.check_id || selectedCheck.id}/return`, returnData);
      toast.success('Check returned to Operations Manager');
      setShowReturnDialog(false);
      setSelectedCheck(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to return check');
    }
  };

  const handleDrillDown = (type) => {
    setDrillDownType(type);
    setShowDrillDown(true);
  };

  const getDrillDownData = () => {
    // For drill-down, show individual subchecks (actual work items for agents)
    switch (drillDownType) {
      case 'total': return { title: 'All Assigned Checks', data: myChecks };
      case 'newly_added': return { title: 'Newly Added (Last 24h)', data: myChecks.filter(c => {
        if (!c.assigned_at && !c.created_at) return false;
        const addedDate = new Date(c.assigned_at || c.created_at);
        const hoursDiff = (new Date() - addedDate) / (1000 * 60 * 60);
        return hoursDiff <= 24;
      })};
      case 'not_initiated': return { title: 'Assigned Checks', data: myChecks.filter(c => !c.verification_progress || c.verification_progress === 'not_initiated') };
      case 'not_sent_vendor': return { title: 'Not Sent to Vendor', data: myChecks.filter(c => !c.vendor_name && (!c.verification_progress || c.verification_progress === 'not_initiated') && !c.is_completed) };
      case 'completed': return { title: 'Completed Checks', data: myChecks.filter(c => ['verified', 'completed', 'clear'].includes(c.status) || c.verification_progress === 'completed') };
      case 'within_tat': return { title: 'Within TAT', data: myChecks.filter(c => c.is_within_tat && !['verified', 'completed', 'clear'].includes(c.status)) };
      case 'over_tat': return { title: 'Over TAT', data: myChecks.filter(c => !c.is_within_tat && !['verified', 'completed', 'clear'].includes(c.status)) };
      case 'insuff': return { title: 'Insufficiency Raised', data: myChecks.filter(c => c.status === 'insufficiency' || c.verification_progress === 'insufficiency') };
      case 'mirrored': return { title: 'Present & Permanent Address Same (Mirrored)', data: myChecks.filter(c => c.permanent_same_as_present && c.sub_option === 'permanent') };
      default: return { title: 'Checks', data: [] };
    }
  };

  // Extra cost request handler
  const handleRaiseExtraCost = async () => {
    if (!extraCostCheck || !extraCostAmount) return;
    setExtraCostLoading(true);
    try {
      await axios.post('/extra-cost/request', {
        case_id: extraCostCheck.case_id,
        check_id: extraCostCheck.check_id || extraCostCheck.id,
        amount: parseFloat(extraCostAmount),
        remarks: extraCostRemarks
      });
      toast.success('Extra cost request submitted to admin');
      setShowExtraCostDialog(false);
      setExtraCostAmount('');
      setExtraCostRemarks('');
      setExtraCostCheck(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setExtraCostLoading(false);
    }
  };

  // Render checks table with multiselect - shows INDIVIDUAL subchecks as separate rows
  // Render checks table - Each sub-check displayed as a SEPARATE ROW
  const renderChecksTable = (checksToShow, title, showActions = false, showCostColumns = false, applyDashboardFilters = true) => {
    // Use checksToShow directly (already expanded subchecks from API) for table display
    // Each sub-check gets its own row - no grouping
    // applyDashboardFilters=false (May 24, 2026) keeps the Completed
    // tab independent of the Dashboard's vendor/client/state filters
    // so users can scope completed work freely.
    const filteredChecks = applyDashboardFilters ? getFilteredChecks(checksToShow) : checksToShow;
    const paginatedChecks = getPaginatedChecks(filteredChecks);
    const allCheckIds = paginatedChecks.map(c => c.check_id || c.id);
    const allSelected = allCheckIds.length > 0 && allCheckIds.every(id => selectedChecks.includes(id));
    const selectedCount = selectedChecks.filter(id => allCheckIds.includes(id)).length;
    
    return (
      <Card className="border-2 border-emerald-100">
        <CardHeader className="pb-2 bg-gradient-to-r from-emerald-50 to-teal-50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-emerald-800 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {title}
            </CardTitle>
            <div className="flex items-center gap-4">
              {/* Bulk Action Bar */}
              {selectedCount > 0 && (
                <div className="flex items-center gap-2 bg-blue-100 px-3 py-1.5 rounded-lg">
                  <CheckSquare className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">{selectedCount} selected</span>
                  <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-white" onClick={() => setShowBulkInitiateDialog(true)} data-testid="bulk-initiate-btn">
                    <Send className="w-3 h-3 mr-1" />Bulk Initiate
                  </Button>
                  <Button size="sm" className="h-7 bg-purple-600 hover:bg-purple-700 text-white" onClick={() => setShowTransferDialog(true)} data-testid="transfer-to-vendor-btn">
                    <Truck className="w-3 h-3 mr-1" />Transfer to Vendor
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => { setUrgentReminderSingleCheck(null); setUrgentReminderResult(null); setShowUrgentReminderDialog(true); }}
                    data-testid="bulk-urgent-reminder-btn"
                  >
                    <Send className="w-3 h-3 mr-1" />Send Reminder
                  </Button>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => setSelectedChecks([])}>Clear</Button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Show:</span>
                <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                {filteredChecks.length} records (sub-checks)
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Pagination - above table */}
          {filteredChecks.length > 0 && (
            <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50" data-testid="pagination-bar">
              <div className="text-sm text-gray-600">
                Showing {Math.min((currentPage - 1) * pageSize + 1, filteredChecks.length)} to {Math.min(currentPage * pageSize, filteredChecks.length)} of {filteredChecks.length} records
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</Button>
                <span className="text-sm px-3">Page {currentPage} of {Math.ceil(filteredChecks.length / pageSize) || 1}</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredChecks.length / pageSize), p + 1))} disabled={currentPage >= Math.ceil(filteredChecks.length / pageSize)}>Next</Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.ceil(filteredChecks.length / pageSize))} disabled={currentPage >= Math.ceil(filteredChecks.length / pageSize)}>Last</Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-indigo-600 to-blue-600">
                  <TableHead className="w-[40px] text-white">
                    <Checkbox 
                      checked={allSelected}
                      onCheckedChange={() => toggleSelectAll(paginatedChecks)}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-indigo-600"
                    />
                  </TableHead>
                  <TableHead className="w-[100px] text-white font-semibold">Case ID</TableHead>
                  <TableHead className="w-[80px] text-white font-semibold">Check ID</TableHead>
                  <TableHead className="text-white font-semibold">Employee</TableHead>
                  <TableHead className="text-white font-semibold">Client</TableHead>
                  <TableHead className="text-white font-semibold">Check Type</TableHead>
                  <TableHead className="text-white font-semibold">Status</TableHead>
                  <TableHead className="text-white font-semibold">Vendor</TableHead>
                  <TableHead className="text-white font-semibold"><div className="text-xs">Agent</div><div className="text-[10px] text-white/70">Remarks</div></TableHead>
                  <TableHead className="text-center text-white font-semibold"><div className="text-xs">Field Agent</div><div className="text-[10px] text-white/70">Name</div></TableHead>
                  <TableHead className="text-center text-white font-semibold"><div className="text-xs">Field Status</div><div className="text-[10px] text-white/70">Agent Remarks</div></TableHead>
                  {showCostColumns && (
                    <>
                      <TableHead className="text-center text-white font-semibold text-xs">Cost</TableHead>
                      <TableHead className="text-center text-white font-semibold text-xs">Extra Cost</TableHead>
                      <TableHead className="text-center text-white font-semibold text-xs">Total</TableHead>
                    </>
                  )}
                  <TableHead className="text-center text-white font-semibold"><div className="text-xs">Vendor</div><div className="text-[10px] text-white/70">Assigned</div></TableHead>
                  <TableHead className="text-center text-white font-semibold"><div className="text-xs">Completed</div><div className="text-[10px] text-white/70">Verified</div></TableHead>
                  <TableHead className="text-center text-white font-semibold"><div className="text-xs">TAT</div><div className="text-[10px] text-white/70">Days/Limit</div></TableHead>
                  {showActions && <TableHead className="w-[220px] text-white font-semibold">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={15} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : paginatedChecks.length === 0 ? (
                  <TableRow><TableCell colSpan={15} className="text-center py-8 text-gray-500">No checks found</TableCell></TableRow>
                ) : paginatedChecks.map((check, idx) => {
                  const CheckIcon = CHECK_TYPE_ICONS[check.check_type] || FileCheck;
                  const vendorName = check.assigned_vendor?.vendor_name || check.assigned_vendor_name || check.vendor_name || (typeof check.assigned_vendor === 'string' ? check.assigned_vendor : null);
                  const checkId = check.check_id || check.id;
                  const fieldAgentName = check.field_agent_name || check.field_agent?.name || '';
                  const isSelected = selectedChecks.includes(checkId);
                  
                  return (
                    <TableRow key={checkId || idx} className={`hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                      <TableCell>
                        <Checkbox 
                          checked={isSelected}
                          onCheckedChange={() => toggleCheckSelection(checkId)}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{check.case_id}</TableCell>
                      <TableCell className="font-mono text-xs text-blue-600">{checkId}</TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{check.employee_name || '-'}</div>
                        <div className="text-xs text-gray-500">{check.employee_email || ''}</div>
                      </TableCell>
                      <TableCell className="text-sm">{check.client_name || '-'}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1">
                            <CheckIcon className="w-3 h-3 text-gray-500" />
                            <span className="text-xs font-medium">{getCheckTypeLabel(check.check_type)}</span>
                          </div>
                          {/* Show actual subcheck types from the data */}
                          {check.subcheck_count > 1 && check.subchecks?.length > 0 ? (
                            <span className="text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                              {check.subchecks.map(sc => sc.subcheck_type || '').filter(Boolean).join(' / ')}
                            </span>
                          ) : check.subcheck_count === 1 && check.subchecks?.[0]?.subcheck_type ? (
                            <span className="text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                              {check.subchecks[0].subcheck_type}
                            </span>
                          ) : (check.subcheck_type || check.sub_check_type) ? (
                            <span className="text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{check.subcheck_type || check.sub_check_type}</span>
                          ) : null}
                          {check.permanent_same_as_present && check.sub_option === 'permanent' && (
                            <span data-testid={`same-as-present-badge-${check.check_id}`} className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                              Same as Present - Mirrored
                            </span>
                          )}
                          {check.synced_from_present && (
                            <span className="text-[9px] text-blue-400 italic">Auto-synced</span>
                          )}
                          {check.address_updated_after_unlink && (
                            <span data-testid={`address-updated-badge-${check.check_id}`} className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-300 font-semibold">
                              Updated address — re-verify
                            </span>
                          )}
                          {/* Inline address preview for address/court checks */}
                          {(check.check_type === 'address_check' || check.check_type === 'criminal_court_check' || check.check_type === 'criminal_advocate_check') && (check.city || check.state || check.pincode) && (
                            <span className="text-[10px] text-gray-600 bg-gray-50 px-1.5 py-0.5 rounded truncate max-w-[200px]" title={[check.city, check.state, check.pincode].filter(Boolean).join(', ')}>
                              {[check.city, check.state].filter(Boolean).join(', ')}{check.pincode ? ` - ${check.pincode}` : ''}
                            </span>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openCheckDetails(check)} className="h-5 w-fit p-0 text-blue-500 hover:text-blue-700 text-[10px]">
                            <Eye className="w-3 h-3 mr-1" />View
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          {/* Show completion status based on subchecks */}
                          <Badge data-testid={`check-status-badge-${check.check_id || idx}`} className={`text-xs ${
                            check.is_completed || check.verification_progress === 'completed' || check.verification_progress === 'verified' ? 'bg-green-100 text-green-800' :
                            check.completed_subcheck_count > 0 ? 'bg-blue-100 text-blue-800' :
                            check.verification_progress === 'initiated' ? 'bg-green-100 text-green-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {check.is_completed ? 'Completed' : 
                             check.completed_subcheck_count > 0 ? `In Progress (${check.completed_subcheck_count}/${check.subcheck_count})` :
                             check.verification_progress ? check.verification_progress.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Not Initiated'}
                          </Badge>
                          {/* "Insuff cleared" sub-label so agents can spot cases where QC just cleared an insufficiency */}
                          {(check.verification_progress === 'in_progress' || check.status === 'in_progress')
                            && check.insufficiency_cleared_date
                            && !check.has_insufficiency
                            && !check.is_completed
                            && (
                              <span data-testid={`check-insuff-cleared-${check.check_id || idx}`} className="text-[10px] font-semibold text-amber-700">
                                Insuff cleared
                              </span>
                          )}
                          {(check.is_completed || check.verification_progress === 'completed' || check.verification_progress === 'verified') && shouldShowResult(check.verification_result) && (
                            <span data-testid={`check-result-label-${check.check_id || idx}`} className={`text-[10px] font-semibold ${getResultTextColor(check.verification_result)}`}>
                              {getResultLabel(check.verification_result)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {vendorName ? (
                          <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700">{vendorName}</Badge>
                        ) : <span className="text-xs text-gray-400">-</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {/* Agent Remarks - FE Residence Confirmed for address, Report Uploaded for non-field checks */}
                        {(() => {
                          // For non-field checks, show "Report Uploaded" if vendor uploaded report
                          if (check.check_type !== 'address_check' && check.check_type !== 'address_verification') {
                            if (check.vendor_report_uploaded) {
                              return (
                                <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Report Uploaded</Badge>
                              );
                            }
                            return <span className="text-gray-400">-</span>;
                          }
                          // For address checks, show residence confirmed status
                          const residenceStatus = check.field_verification?.residence_confirmed || 
                                                  check.address_verification?.field_residence_confirmed;
                          if (!residenceStatus) return <span className="text-gray-400">-</span>;
                          
                          const statusConfig = {
                            'yes': { label: 'Yes', suffix: 'Positive', className: 'bg-green-100 text-green-700' },
                            'no': { label: 'No', suffix: 'Negative', className: 'bg-red-100 text-red-700' },
                            'shifted': { label: 'Shifted', suffix: 'Pending', className: 'bg-amber-100 text-amber-700' },
                            'not_traceable': { label: 'Not Traceable', suffix: 'Insuff', className: 'bg-orange-100 text-orange-700' },
                            'left_job_not_cooperating': { label: 'Left Job/Not Co-operating', suffix: 'Insuff', className: 'bg-orange-100 text-orange-700' },
                            'incorrect_number_switchoff': { label: 'Incorrect Number/Switch Off', suffix: 'Insuff', className: 'bg-orange-100 text-orange-700' }
                          };
                          
                          const config = statusConfig[residenceStatus] || { label: residenceStatus, suffix: '', className: 'bg-gray-100 text-gray-700' };
                          
                          return (
                            <div className="flex flex-col gap-0.5">
                              <Badge className={`text-[10px] ${config.className}`}>
                                {config.label}
                              </Badge>
                              {config.suffix && (
                                <span className={`text-[9px] font-semibold ${
                                  config.suffix === 'Positive' ? 'text-green-600' : 
                                  config.suffix === 'Negative' ? 'text-red-600' : 
                                  'text-orange-600'
                                }`}>
                                  {config.suffix}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-center">
                        {fieldAgentName ? (
                          <div className="text-xs font-medium text-purple-700">{fieldAgentName}</div>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-xs text-gray-400 hover:text-purple-600 h-6 px-2"
                            onClick={() => openAssignFieldAgent(check)}
                          >
                            <UserPlus className="w-3 h-3 mr-1" />Add
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs ${
                          check.field_verification_submitted || check.field_verification_status === 'completed' || check.vendor_report_uploaded
                            ? 'bg-emerald-100 text-emerald-700' 
                            : check.field_verification_status === 'in_progress' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-gray-100 text-gray-600'
                        }`}>
                          {check.field_verification_submitted || check.vendor_report_uploaded
                            ? 'Submitted' 
                            : check.field_verification_status || 'Pending'}
                        </Badge>
                        {check.field_agent_remarks && (
                          <div className="text-[9px] text-gray-500 mt-1 max-w-[80px] truncate" title={check.field_agent_remarks}>
                            {check.field_agent_remarks}
                          </div>
                        )}
                      </TableCell>
                      {showCostColumns && (
                        <>
                          <TableCell className="text-xs text-center font-medium">{check.vendor_cost ? `Rs.${check.vendor_cost}` : '-'}</TableCell>
                          <TableCell className="text-xs text-center font-medium text-amber-600">{check.extra_cost ? `Rs.${check.extra_cost}` : '-'}</TableCell>
                          <TableCell className="text-xs text-center font-bold text-emerald-700">{(check.vendor_cost || check.extra_cost) ? `Rs.${(parseFloat(check.vendor_cost || 0) + parseFloat(check.extra_cost || 0))}` : '-'}</TableCell>
                        </>
                      )}
                      <TableCell className="text-xs text-center text-gray-500">{formatDate(check.vendor_assigned_at || check.assigned_at)}</TableCell>
                      <TableCell className="text-xs text-center text-gray-500">{formatDate(check.completed_at || check.verified_at)}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs ${check.is_within_tat ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {check.days_elapsed || 0}/{check.agent_tat || 7}
                        </Badge>
                      </TableCell>
                      {showActions && (
                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Button variant="outline" size="sm" onClick={() => openCheckDetails(check)} className="text-xs h-7 px-2 text-blue-600 hover:bg-blue-50 border-blue-200">
                              <Eye className="w-3 h-3 mr-1" />View
                            </Button>
                            {user?.role === 'vendor_management' && (
                              <Button variant="outline" size="sm" onClick={() => openUpdateStatus(check)} className="text-xs h-7 px-2" data-testid="update-verification-btn">
                                <Play className="w-3 h-3 mr-1" />Update
                              </Button>
                            )}
                            {(user?.role === 'external_vendor' || user?.role === 'vendor_management') && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => openAssignFieldAgent(check)} 
                                  className="text-xs h-7 px-2 text-purple-600 hover:bg-purple-50 border-purple-200"
                                  title="Assign Field Agent & Share Link"
                                >
                                  <Share2 className="w-3 h-3" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => openFieldVerificationView(check)} className="text-xs h-7 px-2 text-indigo-600 hover:bg-indigo-50 border-indigo-200">
                                  <FileEdit className="w-3 h-3 mr-1" />View Form
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => window.open(generateFieldLink(check), '_blank')}
                                  className="text-xs h-7 px-2 text-teal-600 hover:bg-teal-50 border-teal-200"
                                  title="Open Field Verification Form"
                                >
                                  <ExternalLink className="w-3 h-3 mr-1" />Field Form
                                </Button>
                              </>
                            )}
                            {!check.verification_progress && (
                              <Button variant="outline" size="sm" className="text-xs h-7 px-2 text-green-600 hover:bg-green-50 border-green-200">
                                <Send className="w-3 h-3 mr-1" />Initiate
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreVertical className="w-4 h-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {check.check_type === 'address_check' && (
                                  <>
                                    <DropdownMenuItem onClick={() => copyForFieldAgent(check)} className="text-purple-600 cursor-pointer">
                                      <Copy className="w-4 h-4 mr-2" />Copy Check Details
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                <DropdownMenuItem onClick={() => openRaiseInsuff(check)} className="text-red-600 cursor-pointer">
                                  <AlertTriangle className="w-4 h-4 mr-2" />Raise Insuff
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setExtraCostCheck(check); setShowExtraCostDialog(true); }} className="text-amber-600 cursor-pointer" data-testid="raise-extra-cost-btn">
                                  <CreditCard className="w-4 h-4 mr-2" />Raise Extra Cost
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => { setUrgentReminderSingleCheck(check); setUrgentReminderResult(null); setShowUrgentReminderDialog(true); }}
                                  className="text-red-600 cursor-pointer"
                                  data-testid={`row-urgent-reminder-${check.check_id}`}
                                >
                                  <Send className="w-4 h-4 mr-2" />Send Urgent Reminder
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openReturnDialog(check)} className="text-amber-600 cursor-pointer" data-testid="return-to-ops-btn">
                                  <RotateCcw className="w-4 h-4 mr-2" />Return to Ops Manager
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render filter section
  const renderFilters = () => (
    <Card className="border-2 border-blue-100 mb-4">
      <CardContent className="pt-4">
        {/* Quick Date Filters + Apply/Clear on same row */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Label className="text-sm font-medium mr-2">Quick Filters:</Label>
          {['latest', 'today', 'this_week', 'this_month'].map(type => (
            <Button 
              key={type} 
              size="sm" 
              variant={filters.dateFilterType === type ? 'default' : 'outline'}
              onClick={() => setFilters({...filters, dateFilterType: type})}
              className={filters.dateFilterType === type ? 'bg-indigo-600' : ''}
            >
              {type === 'latest' && <RefreshCw className="w-3 h-3 mr-1" />}
              {type === 'today' && <Calendar className="w-3 h-3 mr-1" />}
              {type === 'this_week' && <Clock className="w-3 h-3 mr-1" />}
              {type === 'this_month' && <BarChart3 className="w-3 h-3 mr-1" />}
              {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={fetchData} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white px-4"><Filter className="w-4 h-4 mr-1" />Apply Filters</Button>
            <Button variant="outline" size="sm" onClick={() => { setFilters({ clientId: 'all', checkType: 'all', status: 'all', tatStatus: 'all', vendorId: 'all', agentId: 'all', fieldStatus: 'all', state: 'all', educationType: 'all', dateFilterType: 'latest', search: '' }); setCurrentPage(1); }} className="border-red-300 text-red-600 hover:bg-red-50 px-4"><RotateCcw className="w-4 h-4 mr-1" />Clear All</Button>
          </div>
        </div>
        {/* Filter dropdowns row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-8 gap-3 mb-3">
          <Select value={filters.clientId} onValueChange={(v) => setFilters({...filters, clientId: v})}>
            <SelectTrigger><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Clients</SelectItem>{clients.map(c => (<SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>))}</SelectContent>
          </Select>
          <Select value={filters.checkType} onValueChange={(v) => setFilters({...filters, checkType: v})}>
            <SelectTrigger><SelectValue placeholder="Check Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem>{CHECK_TYPES.map(ct => (<SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>))}</SelectContent>
          </Select>
          <Select value={filters.vendorId} onValueChange={(v) => setFilters({...filters, vendorId: v})}>
            <SelectTrigger data-testid="filter-vendor"><SelectValue placeholder="All Vendors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {(vendors || []).map(v => (
                <SelectItem key={v.id || v._id} value={v.id || v._id}>{v.vendor_name || v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
            <SelectTrigger><SelectValue placeholder="Verification Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="not_initiated">Not Initiated</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="insufficiency">Insufficiency</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.tatStatus} onValueChange={(v) => setFilters({...filters, tatStatus: v})}>
            <SelectTrigger><SelectValue placeholder="TAT Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All TAT</SelectItem><SelectItem value="within_tat">Within TAT</SelectItem><SelectItem value="over_tat">Over TAT</SelectItem></SelectContent>
          </Select>
          {/* Field Agents filter is relevant only for External Vendors who
              dispatch via in-house field agents. Agent Vendor Team
              (vendor_management) routes via external vendors (the
              All Vendors filter above is what they need), so the agent
              filter is hidden for that role. */}
          {user?.role === 'external_vendor' && (
            <Select value={filters.agentId} onValueChange={(v) => setFilters({...filters, agentId: v})}>
              <SelectTrigger><SelectValue placeholder="All Field Agents" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Field Agents</SelectItem>{fieldAgents.map(a => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}</SelectContent>
            </Select>
          )}
          {(user?.role === 'external_vendor' || user?.role === 'vendor_management') && (
            <>
              <Select value={filters.fieldStatus} onValueChange={(v) => setFilters({...filters, fieldStatus: v})}>
                <SelectTrigger><SelectValue placeholder="Field Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Field Status</SelectItem>
                  <SelectItem value="not_assigned">Not Assigned</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
          <Select value={filters.state} onValueChange={(v) => setFilters({...filters, state: v})}>
            <SelectTrigger data-testid="filter-state"><SelectValue placeholder="All States" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {uniqueStates.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.educationType} onValueChange={(v) => setFilters({...filters, educationType: v})}>
            <SelectTrigger data-testid="filter-education-type"><SelectValue placeholder="Education Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Education Types</SelectItem>
              <SelectItem value="highest">Highest Education</SelectItem>
              <SelectItem value="ssc">SSC (10th)</SelectItem>
              <SelectItem value="hsc">HSC (12th)</SelectItem>
              <SelectItem value="iti">ITI</SelectItem>
              <SelectItem value="diploma">Diploma</SelectItem>
              <SelectItem value="graduation">Graduation</SelectItem>
              <SelectItem value="post graduation">Post Graduation</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Centered search row */}
        <div className="flex justify-center">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search Name, Case ID, Pincode, State, City, SSC, HSC..." className="pl-10" value={filters.search} onChange={(e) => setFilters({...filters, search: e.target.value})} data-testid="search-input" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Render dashboard
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards - Updated Order: Total Assigned, Newly Added, Not Initiated, Not Sent Vendor, Completed, Within TAT, Over TAT, Insuff */}
      {/* Stats count PARENT checks (unique), not expanded subchecks */}
      <div className="flex flex-nowrap gap-2 overflow-x-auto pb-1">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-blue-500 flex-1 min-w-0" onClick={() => handleDrillDown('total')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-gray-500">Total Assigned</p><p className="text-2xl font-bold text-blue-700">{uniqueParentChecks.length}</p></div>
              <PlusCircle className="w-6 h-6 text-blue-500 opacity-50 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-purple-500 flex-1 min-w-0" onClick={() => handleDrillDown('newly_added')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-gray-500">Newly Added</p><p className="text-2xl font-bold text-purple-700">{newlyAddedChecks.length}</p></div>
              <PlusCircle className="w-6 h-6 text-purple-500 opacity-50 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-orange-500 flex-1 min-w-0" onClick={() => handleDrillDown('not_initiated')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-gray-500">Assigned</p><p className="text-2xl font-bold text-orange-700">{notInitiatedChecks.length}</p></div>
              <Timer className="w-6 h-6 text-orange-500 opacity-50 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-rose-500 flex-1 min-w-0" onClick={() => handleDrillDown('not_sent_vendor')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-gray-500 whitespace-nowrap">Not Sent Vendor</p><p className="text-2xl font-bold text-rose-700">{notSentToVendorChecks.length}</p></div>
              <Send className="w-6 h-6 text-rose-500 opacity-50 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-green-500 flex-1 min-w-0" onClick={() => handleDrillDown('completed')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-gray-500">Completed</p><p className="text-2xl font-bold text-green-700">{completedChecks.length}</p></div>
              <CheckCircle className="w-6 h-6 text-green-500 opacity-50 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-emerald-500 flex-1 min-w-0" onClick={() => handleDrillDown('within_tat')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-gray-500">Within TAT</p><p className="text-2xl font-bold text-emerald-700">{withinTatChecks.length}</p></div>
              <Calendar className="w-6 h-6 text-emerald-500 opacity-50 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-red-500 flex-1 min-w-0" onClick={() => handleDrillDown('over_tat')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-gray-500">Over TAT</p><p className="text-2xl font-bold text-red-700">{overTatChecks.length}</p></div>
              <Clock className="w-6 h-6 text-red-500 opacity-50 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-amber-500 flex-1 min-w-0" onClick={() => handleDrillDown('insuff')}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-gray-500">Insuff</p><p className="text-2xl font-bold text-amber-700">{insuffCount}</p></div>
              <AlertTriangle className="w-6 h-6 text-amber-500 opacity-50 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
        {myChecks.filter(c => c.permanent_same_as_present && c.sub_option === 'permanent').length > 0 && (
          <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-blue-400 bg-gradient-to-br from-blue-50 to-blue-100 flex-1 min-w-0" onClick={() => handleDrillDown('mirrored')} data-testid="stat-mirrored-address">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-blue-600 whitespace-nowrap">Address Same</p><p className="text-2xl font-bold text-blue-700">{myChecks.filter(c => c.permanent_same_as_present && c.sub_option === 'permanent').length}</p></div>
                <Copy className="w-6 h-6 text-blue-400 opacity-50 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filters */}
      {renderFilters()}

      {/* Recent Checks */}
      {renderChecksTable(myChecks, 'Recent Assigned Checks', true)}
    </div>
  );

  // Render Client TAT Summary as standalone tab
  const renderClientTatSummary = () => (
    <Card className="border-2 border-amber-100">
      <CardHeader className="pb-2 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
          <Clock className="w-5 h-5" />Client-wise Check TAT Summary
          <Badge variant="outline" className="ml-2 text-xs">Agent TAT = Client TAT - 2 days</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {Object.keys(clientTatSummary).length === 0 ? (
          <div className="text-center py-8 text-gray-500">No data available</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-indigo-600 to-blue-600">
                <TableHead className="text-white font-semibold">Client</TableHead>
                <TableHead className="text-center text-white font-semibold">Total Checks</TableHead>
                <TableHead className="text-center text-white font-semibold">Within TAT</TableHead>
                <TableHead className="text-center text-white font-semibold">Over TAT</TableHead>
                <TableHead className="text-white font-semibold">Check Type Breakdown</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(clientTatSummary).map(([client, data]) => (
                <TableRow key={client} className="hover:bg-slate-50">
                  <TableCell className="font-medium">{client}</TableCell>
                  <TableCell className="text-center"><Badge variant="outline">{data.total}</Badge></TableCell>
                  <TableCell className="text-center"><Badge className="bg-green-100 text-green-800">{data.within_tat}</Badge></TableCell>
                  <TableCell className="text-center"><Badge className="bg-red-100 text-red-800">{data.over_tat}</Badge></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(data.check_types).map(([type, counts]) => (
                        <Badge key={type} variant="outline" className="text-xs">{getCheckTypeLabel(type)}: {counts.within_tat}/{counts.total}</Badge>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'my-checks':
        return (
          <div className="space-y-4">
            {renderFilters()}
            {renderChecksTable(myChecks, 'My Assigned Checks', true)}
          </div>
        );
      case 'all-checks':
        return (
          <div className="space-y-4">
            {renderFilters()}
            {renderChecksTable(checks, 'All Checks', true)}
          </div>
        );
      case 'completed': return (
        <div className="space-y-4">
          {/* Export Section */}
          <Card className="border-2 border-green-100">
            <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-green-800 flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Export Completed Checks
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="flex flex-wrap items-end gap-4">
                {/* Date Filter Buttons */}
                <div className="flex flex-wrap items-center gap-2">
                  <Label className="text-sm font-medium mr-2">Date Filter:</Label>
                  {/* May 30, 2026 — 'Monthly' removed per user request.
                      'Last Month' (calendar-month) covers the same
                      semantic without the confusing rolling-month
                      ambiguity. */}
                  {['latest', 'today', 'weekly', 'last_month', 'between'].map(type => (
                    <Button 
                      key={type} 
                      size="sm" 
                      variant={exportDateFilter === type ? 'default' : 'outline'}
                      onClick={() => setExportDateFilter(type)}
                      className={exportDateFilter === type ? 'bg-green-600 hover:bg-green-700' : ''}
                    >
                      {type === 'latest' && <RefreshCw className="w-3 h-3 mr-1" />}
                      {type === 'today' && <Calendar className="w-3 h-3 mr-1" />}
                      {type === 'weekly' && <Clock className="w-3 h-3 mr-1" />}
                      {type === 'last_month' && <BarChart3 className="w-3 h-3 mr-1" />}
                      {type === 'between' && <Filter className="w-3 h-3 mr-1" />}
                      {type === 'last_month' ? 'Last Month' : type.charAt(0).toUpperCase() + type.slice(1)}
                    </Button>
                  ))}
                </div>
                
                {/* Vendor filter (May 24, 2026) — independent of the
                    Dashboard's vendor filter so admins can scope
                    completed work to any vendor regardless of the
                    current Dashboard state. 'inherit' means whatever
                    the Dashboard is showing. */}
                <div className="flex items-center gap-2" data-testid="completed-vendor-filter-wrap">
                  <Label className="text-sm font-medium">Vendor:</Label>
                  <Select value={completedVendorFilter} onValueChange={setCompletedVendorFilter}>
                    <SelectTrigger className="w-[240px] h-9" data-testid="completed-vendor-filter">
                      <SelectValue placeholder="All Vendors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vendors</SelectItem>
                      <SelectItem value="inherit">Inherit from Dashboard</SelectItem>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.vendor_name || v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Between Dates */}
                {exportDateFilter === 'between' && (
                  <div className="flex items-center gap-2">
                    <div>
                      <Label className="text-xs">From</Label>
                      <Input 
                        type="date" 
                        value={exportDateFrom} 
                        onChange={(e) => setExportDateFrom(e.target.value)}
                        className="w-36"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">To</Label>
                      <Input 
                        type="date" 
                        value={exportDateTo} 
                        onChange={(e) => setExportDateTo(e.target.value)}
                        className="w-36"
                      />
                    </div>
                  </div>
                )}
                
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleExportCompletedChecks}
                  disabled={exportLoading || (exportDateFilter === 'between' && (!exportDateFrom || !exportDateTo))}
                  data-testid="export-completed-excel-btn"
                >
                  {exportLoading ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Exporting...</>
                  ) : (
                    <><Download className="w-4 h-4 mr-2" />Export Excel</>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Exports the rows currently shown below (same date + vendor filter). Multi-sheet .xlsx: one worksheet per check type, plus an All Checks summary. Columns include Address, State, Pin Code, Vendor Cost &amp; Total. Dates render as DD-MM-YYYY.
              </p>
            </CardContent>
          </Card>
          
          {/* Completed Checks Table — applyDashboardFilters=false so
              the Completed tab uses its own vendor / date scope
              independent of the Dashboard (May 24, 2026). */}
          {renderChecksTable(filteredCompletedChecks, `Completed Checks (${filteredCompletedChecks.length})`, false, true, false)}
        </div>
      );
      case 'client-tat': return renderClientTatSummary();
      case 'field-agents': return renderFieldAgentManagement();
      case 'reports': return <VendorFieldAgentReport checks={myChecks} onRefresh={fetchData} />;
      default: return renderDashboard();
    }
  };

  // Field Agent Management Functions
  const handleAddPincode = async (agentId) => {
    if (!newPincode.trim()) {
      toast.error('Please enter a pincode');
      return;
    }
    try {
      await axios.post(`/vendor/field-agents/${agentId}/pincodes`, {
        action: 'add',
        pincodes: [newPincode.trim()]
      });
      toast.success('Pincode added');
      setNewPincode('');
      // Refresh field agents
      const res = await axios.get('/vendor/field-agents');
      setFieldAgents(res.data || []);
    } catch (error) {
      toast.error('Failed to add pincode');
    }
  };

  const handleRemovePincode = async (agentId, pincode) => {
    try {
      await axios.post(`/vendor/field-agents/${agentId}/pincodes`, {
        action: 'remove',
        pincodes: [pincode]
      });
      toast.success('Pincode removed');
      // Refresh field agents
      const res = await axios.get('/vendor/field-agents');
      setFieldAgents(res.data || []);
    } catch (error) {
      toast.error('Failed to remove pincode');
    }
  };

  // Create new field agent from Add Field Agent dialog
  const handleCreateFieldAgent = async () => {
    if (!newAgentForm.name.trim()) {
      toast.error('Please enter Field Agent name');
      return;
    }
    
    try {
      // Parse pincodes from comma-separated string
      const pincodesList = newAgentForm.pincodes
        .split(',')
        .map(p => p.trim())
        .filter(p => p.length > 0);
      
      await axios.post('/vendor/field-agents', {
        name: newAgentForm.name.trim(),
        phone: newAgentForm.phone.trim(),
        pincodes: pincodesList
      });
      
      toast.success('Field Agent created successfully');
      setShowAddAgentDialog(false);
      setNewAgentForm({ name: '', phone: '', pincodes: '' });
      
      // Refresh field agents list
      const res = await axios.get('/vendor/field-agents');
      setFieldAgents(res.data || []);
    } catch (error) {
      console.error('Failed to create field agent:', error);
      toast.error('Failed to create field agent');
    }
  };

  // Deactivate field agent (soft delete - data retained)
  const handleDeactivateFieldAgent = async (agentId, agentName) => {
    if (!window.confirm(`Are you sure you want to deactivate "${agentName}"? This agent will no longer be available for assignment but data will be retained.`)) {
      return;
    }
    
    try {
      await axios.delete(`/vendor/field-agents/${agentId}`);
      toast.success(`Field Agent "${agentName}" deactivated`);
      
      // Refresh field agents list
      const res = await axios.get('/vendor/field-agents');
      setFieldAgents(res.data || []);
    } catch (error) {
      console.error('Failed to deactivate field agent:', error);
      toast.error('Failed to deactivate field agent');
    }
  };

  const renderFieldAgentManagement = () => (
    <div className="space-y-6">
      <Card className="border-2 border-indigo-100">
        <CardHeader className="pb-2 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-indigo-800 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Field Agent Management
            </CardTitle>
            <Button 
              size="sm" 
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => {
                setNewAgentForm({ name: '', phone: '', pincodes: '' });
                setShowAddAgentDialog(true);
              }}
            >
              <PlusCircle className="w-4 h-4 mr-2" />Add Field Agent
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Manage field agents and their assigned pincodes. Auto-Assign mode will automatically assign checks to field agents based on pincode matching.
          </p>
        </CardHeader>
        <CardContent className="pt-4">
          {fieldAgents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No field agents yet. Add your first field agent to enable auto-assignment by pincode.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {fieldAgents.map(agent => {
                const agentPincodes = agent.pincodes || (agent.pincode ? [agent.pincode] : []);
                return (
                  <Card key={agent.id} className="border border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="bg-indigo-100 p-2 rounded-full">
                                <User className="w-5 h-5 text-indigo-600" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900">{agent.name}</h4>
                                {agent.phone && <p className="text-sm text-gray-500">{agent.phone}</p>}
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-7 text-xs text-red-600 hover:bg-red-50 border-red-200"
                              onClick={() => handleDeactivateFieldAgent(agent.id, agent.name)}
                              data-testid={`deactivate-agent-${agent.id}`}
                            >
                              <X className="w-3 h-3 mr-1" />Deactivate
                            </Button>
                          </div>
                          
                          {/* Pincodes Section */}
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              Assigned Pincodes ({agentPincodes.length})
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {agentPincodes.map(pc => (
                                <span 
                                  key={pc} 
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 text-sm rounded-md border border-indigo-200"
                                >
                                  {pc}
                                  <button 
                                    onClick={() => handleRemovePincode(agent.id, pc)}
                                    className="text-indigo-400 hover:text-red-500 ml-1"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                              {agentPincodes.length === 0 && (
                                <span className="text-xs text-gray-400 italic">No pincodes assigned</span>
                              )}
                            </div>
                            
                            {/* Add Pincode Input */}
                            <div className="flex items-center gap-2 mt-3">
                              <Input 
                                placeholder="Add pincode (e.g., 560001)"
                                className="w-40 h-8 text-sm"
                                value={editingAgent?.id === agent.id ? newPincode : ''}
                                onChange={(e) => {
                                  setEditingAgent({ id: agent.id });
                                  setNewPincode(e.target.value);
                                }}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleAddPincode(agent.id);
                                  }
                                }}
                              />
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="h-8"
                                onClick={() => handleAddPincode(agent.id)}
                                disabled={editingAgent?.id !== agent.id || !newPincode.trim()}
                              >
                                <PlusCircle className="w-3 h-3 mr-1" />Add
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Auto-Assign Info Card */}
      <Card className="border border-green-200 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="bg-green-100 p-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-green-800">How Auto-Assign Works</h4>
              <ul className="text-sm text-green-700 mt-2 space-y-1">
                <li>• When you enable "Auto-Assign Mode" in Bulk Initiate, the system matches check pincodes with field agent pincodes</li>
                <li>• A field agent can be assigned multiple pincodes to handle different areas</li>
                <li>• Checks with matching pincodes are automatically assigned to the corresponding field agent</li>
                <li>• Unmatched checks can be manually assigned later</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Header */}
      <div className="bg-gradient-to-r from-indigo-700 via-purple-700 to-blue-700 text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg"><Truck className="w-6 h-6" /></div>
            <div>
              <h1 className="text-xl font-bold">
                {user?.vendor_id ? (vendors.find(v => v.id === user.vendor_id)?.vendor_name || vendors.find(v => v.id === user.vendor_id)?.name || 'Vendor') : 'Agent Vendor Team'}
              </h1>
              <p className="text-xs text-white/70">
                {user?.vendor_id ? 'External Vendor - Field Verifications' : 'Address, Criminal, ID & External Verifications'}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-2">
            <div className="bg-white/20 p-2 rounded-full"><User className="w-5 h-5" /></div>
            <div className="text-right">
              <p className="text-sm font-semibold">{user?.name || 'Agent'}</p>
              <p className="text-xs text-white/70">{user?.email || ''}</p>
            </div>
          </div>
          <div className="bg-white/10 rounded-full p-1"><NotificationCenter /></div>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 bg-purple-600/40" onClick={() => setShowBulkUpload(true)}><Upload className="w-4 h-4 mr-2" />Bulk Upload</Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 bg-blue-600/40" onClick={handleExportVendorExcel} disabled={excelExportLoading} data-testid="export-vendor-excel-btn">
            {excelExportLoading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Exporting...</> : <><Download className="w-4 h-4 mr-2" />Export Excel</>}
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 border border-white/30" onClick={onLogout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 border-b px-6 py-3 shadow-sm">
        <div className="flex gap-2 flex-wrap">
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button key={item.id} onClick={() => setActiveTab(item.id)}
                className={`px-4 py-2 flex items-center gap-2 text-sm font-semibold rounded-lg transition-all duration-200 border ${
                  isActive 
                    ? `${item.bgColor} ${item.textColor} shadow-md border-${item.color}-300 ring-2 ring-offset-1 ring-${item.color}-300`
                    : `bg-white text-gray-600 border-gray-200 hover:${item.bgColor} hover:${item.textColor}`
                }`}
                data-testid={`tab-${item.id}`}>
                <Icon className={`w-4 h-4 ${isActive ? item.textColor : 'text-gray-400'}`} /><span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">{renderContent()}</main>

      {/* Drill Down Dialog */}
      <Dialog open={showDrillDown} onOpenChange={setShowDrillDown}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{getDrillDownData().title}<Badge>{getDrillDownData().data.length}</Badge></DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-indigo-600 to-blue-600">
                  <TableHead className="text-white font-semibold">Case ID</TableHead>
                  <TableHead className="text-white font-semibold">Employee</TableHead>
                  <TableHead className="text-white font-semibold">Client</TableHead>
                  <TableHead className="text-white font-semibold">Check Type</TableHead>
                  <TableHead className="text-white font-semibold">Status</TableHead>
                  <TableHead className="text-white font-semibold">TAT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getDrillDownData().data.map((check, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono text-xs">{check.case_id}</TableCell>
                    <TableCell className="text-sm">{check.employee_name}</TableCell>
                    <TableCell className="text-sm">{check.client_name || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{getCheckTypeLabel(check.check_type)}</Badge></TableCell>
                    <TableCell><Badge className={`text-xs ${STATUS_COLORS[check.status] || 'bg-gray-100'}`}>{check.verification_progress || 'Not Initiated'}</Badge></TableCell>
                    <TableCell><Badge className={`text-xs ${check.is_within_tat ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{check.days_elapsed || 0}/{check.agent_tat || 7}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Check Details Dialog with Document Preview */}
      <Dialog open={showCheckDetails} onOpenChange={(open) => { if (!open) { setShowCheckDetails(false); setPreviewDoc(null); setCheckDocuments([]); setCheckDetailData(null); setSelectedSubcheck(null); } }}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-hidden">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5" />Check Details & Document Review</DialogTitle></DialogHeader>
          {selectedCheck && (
            <div className="flex gap-4" style={{ height: 'calc(90vh - 100px)' }}>
              {/* Left Side: Check Information & Subchecks */}
              <div className={`${previewDoc ? 'w-1/2' : 'w-full'} overflow-auto space-y-3 pr-2`}>
                {/* Basic Info Row */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-xs text-slate-600">Case Info</h4>
                    <div className="space-y-1 text-xs">
                      <div><span className="text-gray-500">Case:</span> <span className="font-mono">{selectedCheck.case_id}</span></div>
                      <div><span className="text-gray-500">Check:</span> <span className="font-mono text-blue-600">{selectedCheck.check_id || selectedCheck.id}</span></div>
                      <div><Badge variant="outline" className="text-xs">{getCheckTypeLabel(selectedCheck.check_type)}</Badge></div>
                      <div><Badge className={`${STATUS_COLORS[selectedCheck.status] || 'bg-gray-100'} text-xs`}>{selectedCheck.verification_progress || selectedCheck.status || 'Not Initiated'}</Badge></div>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-xs text-slate-600">Employee</h4>
                    <div className="space-y-1 text-xs">
                      <div className="font-medium">{selectedCheck.employee_name || checkDetailData?.employee?.name}</div>
                      <div className="text-gray-500">{selectedCheck.employee_email || checkDetailData?.employee?.email}</div>
                      {(checkDetailData?.employee?.dob || selectedCheck.dob) && (
                        <div className="text-gray-500">DOB: {checkDetailData?.employee?.dob || selectedCheck.dob}</div>
                      )}
                      <div className="text-gray-500">{selectedCheck.phone || checkDetailData?.employee?.phone || '-'}</div>
                      {(selectedCheck.alternate_contact || checkDetailData?.employee?.alternate_contact) && (
                        <div className="text-gray-500">Alt: {selectedCheck.alternate_contact || checkDetailData?.employee?.alternate_contact}</div>
                      )}
                      <div className="text-gray-500">{selectedCheck.client_name}</div>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-xs text-slate-600">TAT & Assignment</h4>
                    <div className="space-y-1 text-xs">
                      <div><Badge className={selectedCheck.is_within_tat ? 'bg-green-100 text-green-800 text-xs' : 'bg-red-100 text-red-800 text-xs'}>{selectedCheck.days_elapsed || 0}/{selectedCheck.agent_tat || 7}d</Badge></div>
                      <div><span className="text-gray-500">Assigned:</span> {formatDate(selectedCheck.vendor_assigned_at || selectedCheck.assigned_at)}</div>
                      <div><span className="text-gray-500">Vendor:</span> {selectedCheck.assigned_vendor?.vendor_name || selectedCheck.vendor_name || '-'}</div>
                    </div>
                  </div>
                </div>

                {/* Insufficiency History — visibility for agents to know what was raised
                    by them or QC, and when it was cleared. Mirrors QC Case Review card. */}
                {Array.isArray(selectedCheck.insufficiency_history) && selectedCheck.insufficiency_history.length > 0 && (
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="agent-check-insuff-history">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-slate-600" />
                      <p className="font-semibold text-slate-700 text-sm">Insufficiency History ({selectedCheck.insufficiency_history.length})</p>
                    </div>
                    <div className="space-y-2">
                      {selectedCheck.insufficiency_history.map((hist, hidx) => (
                        <div key={hidx} className="p-2.5 bg-white border border-slate-200 rounded-md text-xs">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">
                              <CheckCircle className="w-3 h-3 mr-1" /> Cleared
                            </Badge>
                            {hist.entry_name && <span className="text-slate-500">{hist.entry_name}</span>}
                          </div>
                          <p className="text-slate-700"><strong>Reason:</strong> {hist.reason || '-'}</p>
                          {hist.description && <p className="text-slate-600"><strong>Details:</strong> {hist.description}</p>}
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[11px] text-slate-500">
                            <span>Raised: {hist.raised_at ? new Date(hist.raised_at).toLocaleDateString('en-GB') : 'N/A'}{hist.raised_by_name ? ` by ${hist.raised_by_name}` : ''}{hist.raised_by_role ? ` (${hist.raised_by_role.replace(/_/g, ' ')})` : ''}</span>
                            <span>Cleared: {hist.cleared_at ? new Date(hist.cleared_at).toLocaleDateString('en-GB') : 'N/A'}{hist.cleared_by_name ? ` by ${hist.cleared_by_name}` : ''}{hist.cleared_by_role ? ` (${hist.cleared_by_role.replace(/_/g, ' ')})` : ''}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Candidate Information for Criminal Court / Criminal Advocate / Credit / Database checks */}
                {(selectedCheck.check_type === 'criminal_court_check' || selectedCheck.check_type === 'criminal_advocate_check' || selectedCheck.check_type === 'credit_check' || selectedCheck.check_type === 'global_database' || selectedCheck.check_type === 'india_database') && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-semibold mb-2 text-sm text-amber-800">Candidate Information</h4>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-gray-500">Name:</span>
                        <span className="ml-1 font-medium">{selectedCheck.employee_name || checkDetailData?.employee?.name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Date of Birth:</span>
                        <span className="ml-1 font-medium">{selectedCheck.dob || checkDetailData?.employee?.dob || checkDetailData?.employee?.date_of_birth || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Father's Name:</span>
                        <span className="ml-1 font-medium">{selectedCheck.father_name || checkDetailData?.employee?.father_name || checkDetailData?.employee?.fathers_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Gender:</span>
                        <span className="ml-1 font-medium capitalize">{selectedCheck.gender || checkDetailData?.employee?.gender || '-'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sub-Check Data Section - Using data from selectedCheck directly */}
                {selectedCheck.has_subcheck && selectedCheck.subcheck_data && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold mb-2 text-sm text-green-800">
                      {selectedCheck.subcheck_type || getCheckTypeLabel(selectedCheck.check_type)} Details
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      {/* Address Check Details (also used for criminal court/advocate checks which are address-based) */}
                      {(selectedCheck.check_type === 'address_check' || selectedCheck.check_type === 'address_verification' || selectedCheck.check_type === 'criminal_court_check' || selectedCheck.check_type === 'criminal_advocate_check') && (
                        <>
                          <div className="col-span-2">
                            <span className="text-gray-500">Full Address:</span>
                            <p className="font-medium mt-1">{selectedCheck.subcheck_data.complete_address || selectedCheck.subcheck_data.address_line1 || selectedCheck.subcheck_location || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">City:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.city || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">State:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.state || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Pincode:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.pincode || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Address Type:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.address_type || selectedCheck.subcheck_type || '-'}</span>
                          </div>
                          {selectedCheck.subcheck_data.landmark && (
                            <div className="col-span-2">
                              <span className="text-gray-500">Landmark:</span>
                              <span className="ml-1 font-medium">{selectedCheck.subcheck_data.landmark}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500">Contact Number:</span>
                            <span className="ml-1 font-medium">{selectedCheck.phone || checkDetailData?.employee?.phone || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Alt Contact:</span>
                            <span className="ml-1 font-medium">{selectedCheck.alternate_contact || checkDetailData?.employee?.alternate_contact || '-'}</span>
                          </div>
                        </>
                      )}
                      
                      {/* Education Check Details */}
                      {selectedCheck.check_type === 'education_check' && (
                        <>
                          <div>
                            <span className="text-gray-500">University/Institution:</span>
                            <p className="font-medium">{selectedCheck.subcheck_data.university_name || selectedCheck.subcheck_data.institution_name || selectedCheck.subcheck_institution || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">College:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.college || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Course/Degree:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.course || selectedCheck.subcheck_data.degree || selectedCheck.subcheck_detail || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Year of Passing:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.year_of_passing || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Roll No:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.roll_number || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Grade/CGPA:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.grade || selectedCheck.subcheck_data.cgpa || '-'}</span>
                          </div>
                        </>
                      )}
                      
                      {/* Employment Check Details */}
                      {selectedCheck.check_type === 'employment_check' && (
                        <>
                          <div className="col-span-2">
                            <span className="text-gray-500">Company:</span>
                            <p className="font-medium">{selectedCheck.subcheck_data.company_name || selectedCheck.subcheck_company || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Designation:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.designation || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Employee ID:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.employee_id || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">From:</span>
                            <span className="ml-1 font-medium">{formatDateDDMonYYYY(selectedCheck.subcheck_data.employed_from || selectedCheck.subcheck_data.start_date) || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">To:</span>
                            <span className="ml-1 font-medium">{(selectedCheck.subcheck_data.employed_to || selectedCheck.subcheck_data.end_date) ? formatDateDDMonYYYY(selectedCheck.subcheck_data.employed_to || selectedCheck.subcheck_data.end_date) : 'Present'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">RM:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.rm_name || '-'}</span>
                          </div>
                          {(selectedCheck.subcheck_data.rm_contact_number || selectedCheck.subcheck_data.rm_contact || selectedCheck.subcheck_data.rm_phone) && (
                            <div>
                              <span className="text-gray-500">RM Contact:</span>
                              <span className="ml-1 font-medium">{selectedCheck.subcheck_data.rm_contact_number || selectedCheck.subcheck_data.rm_contact || selectedCheck.subcheck_data.rm_phone}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500">RM Email:</span>
                            <span className="ml-1 font-medium text-blue-600">{selectedCheck.subcheck_rm_email || selectedCheck.subcheck_data.rm_email || '-'}</span>
                          </div>
                          {(selectedCheck.subcheck_data.hr_name) && (
                            <div>
                              <span className="text-gray-500">HR Name:</span>
                              <span className="ml-1 font-medium">{selectedCheck.subcheck_data.hr_name}</span>
                            </div>
                          )}
                          {(selectedCheck.subcheck_data.hr_contact_number || selectedCheck.subcheck_data.hr_contact || selectedCheck.subcheck_data.hr_phone) && (
                            <div>
                              <span className="text-gray-500">HR Contact:</span>
                              <span className="ml-1 font-medium">{selectedCheck.subcheck_data.hr_contact_number || selectedCheck.subcheck_data.hr_contact || selectedCheck.subcheck_data.hr_phone}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500">HR Email:</span>
                            <span className="ml-1 font-medium text-blue-600">{selectedCheck.subcheck_hr_email || selectedCheck.subcheck_data.hr_email || '-'}</span>
                          </div>
                          {(selectedCheck.subcheck_data.salary_per_month || selectedCheck.subcheck_data.ctc || selectedCheck.subcheck_data.salary) && (
                            <div>
                              <span className="text-gray-500">CTC:</span>
                              <span className="ml-1 font-medium">{selectedCheck.subcheck_data.salary_per_month || selectedCheck.subcheck_data.ctc || selectedCheck.subcheck_data.salary}</span>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Reference Check Details */}
                      {(selectedCheck.check_type === 'reference_check' || selectedCheck.check_type === 'personal_reference' || selectedCheck.check_type === 'professional_reference') && (
                        <>
                          <div>
                            <span className="text-gray-500">Reference Name:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.name || selectedCheck.subcheck_name || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Phone:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.phone || selectedCheck.subcheck_detail || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Email:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.email || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Relationship:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.relationship || '-'}</span>
                          </div>
                        </>
                      )}
                      
                      {/* ID/Document Check Details */}
                      {(selectedCheck.check_type === 'id_check' || selectedCheck.check_type === 'id_verification') && (
                        <>
                          <div>
                            <span className="text-gray-500">ID Type:</span>
                            <span className="ml-1 font-medium capitalize">{selectedCheck.sub_option || selectedCheck.subcheck_id_type || selectedCheck.subcheck_data?.document_type || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">ID Number:</span>
                            <span className="ml-1 font-medium">
                              {(() => {
                                const sub = selectedCheck.sub_option?.toLowerCase();
                                const sd = selectedCheck.subcheck_data || {};
                                // Try subcheck_data first, then employee-level fields
                                if (sub === 'pan') return sd.pan_number || selectedCheck.pan_number || '-';
                                if (sub === 'aadhar') return sd.aadhar_number || selectedCheck.aadhar_number || '-';
                                if (sub === 'driving_license' || sub === 'dl') return sd.dl_number || selectedCheck.dl_number || '-';
                                if (sub === 'passport') return sd.passport_number || selectedCheck.passport_number || '-';
                                return sd.id_number || sd.pan_number || sd.aadhar_number || sd.dl_number || sd.passport_number || '-';
                              })()}
                            </span>
                          </div>
                          {/* Show uploaded ID document if available */}
                          {(() => {
                            const sub = selectedCheck.sub_option?.toLowerCase();
                            const docUrl = sub === 'pan' ? selectedCheck.pan_card_url
                              : sub === 'aadhar' ? selectedCheck.aadhar_card_url
                              : null;
                            if (!docUrl) return null;
                            return (
                              <div className="col-span-2 mt-1">
                                <span className="text-gray-500 text-xs">Uploaded Document:</span>
                                <a href={docUrl.startsWith('http') ? docUrl : `${axios.defaults.baseURL}${docUrl}`} target="_blank" rel="noopener noreferrer"
                                  className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 underline">
                                  <Eye className="w-3 h-3" />View {sub === 'pan' ? 'PAN Card' : 'Aadhar Card'}
                                </a>
                              </div>
                            );
                          })()}
                        </>
                      )}
                      
                      {/* Criminal Check Details */}
                      {(selectedCheck.check_type === 'criminal_check' || selectedCheck.check_type === 'criminal_court_check' || selectedCheck.check_type === 'court_advocate_check') && (
                        <>
                          <div className="col-span-2">
                            <span className="text-gray-500">Location:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_location || selectedCheck.subcheck_data.location || '-'}</span>
                          </div>
                        </>
                      )}

                      {/* Generic fallback - show all subcheck_data fields */}
                      {!['address_check', 'address_verification', 'education_check', 'employment_check', 'reference_check', 'personal_reference', 'professional_reference', 'id_check', 'id_verification', 'criminal_check', 'criminal_court_check', 'court_advocate_check', 'criminal_advocate_check', 'credit_check', 'global_database', 'india_database'].includes(selectedCheck.check_type) && (
                        <>
                          {Object.entries(selectedCheck.subcheck_data).filter(([key, value]) => 
                            value && !['id', 'status', 'type', 'created_at', 'updated_at'].includes(key)
                          ).map(([key, value]) => (
                            <div key={key}>
                              <span className="text-gray-500">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>
                              <span className="ml-1 font-medium">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Quick Info Section for checks without subcheck_data but with subcheck fields */}
                {selectedCheck.has_subcheck && !selectedCheck.subcheck_data && (selectedCheck.subcheck_location || selectedCheck.subcheck_institution || selectedCheck.subcheck_company || selectedCheck.subcheck_name) && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold mb-2 text-sm text-green-800">
                      {selectedCheck.subcheck_type || getCheckTypeLabel(selectedCheck.check_type)} Details
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {selectedCheck.subcheck_location && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Location:</span>
                          <span className="ml-1 font-medium">{selectedCheck.subcheck_location}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_institution && (
                        <div>
                          <span className="text-gray-500">Institution:</span>
                          <span className="ml-1 font-medium">{selectedCheck.subcheck_institution}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_company && (
                        <div>
                          <span className="text-gray-500">Company:</span>
                          <span className="ml-1 font-medium">{selectedCheck.subcheck_company}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_name && (
                        <div>
                          <span className="text-gray-500">Name:</span>
                          <span className="ml-1 font-medium">{selectedCheck.subcheck_name}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_detail && (
                        <div>
                          <span className="text-gray-500">Detail:</span>
                          <span className="ml-1 font-medium">{selectedCheck.subcheck_detail}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_hr_email && (
                        <div>
                          <span className="text-gray-500">HR Email:</span>
                          <span className="ml-1 font-medium text-blue-600">{selectedCheck.subcheck_hr_email}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_rm_email && (
                        <div>
                          <span className="text-gray-500">RM Email:</span>
                          <span className="ml-1 font-medium text-blue-600">{selectedCheck.subcheck_rm_email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Fallback to API-fetched data if no local subcheck data */}
                {!selectedCheck.has_subcheck && checkDetailData?.employee && (() => {
                  const subchecks = getSubchecks(selectedCheck.check_type, checkDetailData.employee);
                  if (subchecks.length === 0) return null;
                  return (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-semibold mb-2 text-sm text-green-800">{getCheckTypeLabel(selectedCheck.check_type)} Data</h4>
                      <div className="space-y-2">
                        {subchecks.map((subcheck, idx) => (
                          <div key={idx} className="p-2 bg-white rounded border cursor-pointer hover:bg-green-100" onClick={() => setSelectedSubcheck(idx)}>
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-sm">{subcheck.type}</span>
                              <Eye className="w-4 h-4 text-gray-400" />
                            </div>
                            {(selectedCheck.check_type === 'address_check' || selectedCheck.check_type === 'address_verification') && 
                              <p className="text-xs text-gray-500 truncate">{subcheck.complete_address || subcheck.address_line1}, {subcheck.city}</p>}
                            {selectedCheck.check_type === 'education_check' && 
                              <p className="text-xs text-gray-500 truncate">{subcheck.university_name || subcheck.institution_name} - {subcheck.course || subcheck.degree}</p>}
                            {selectedCheck.check_type === 'employment_check' && 
                              <p className="text-xs text-gray-500 truncate">{subcheck.company_name} - {subcheck.designation}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Ultimate Fallback: Show check details from case data when no subcheck info available */}
                {!selectedCheck.has_subcheck && checkDetailData && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-semibold mb-2 text-sm text-amber-800">
                      {getCheckTypeLabel(selectedCheck.check_type)} - Candidate Details
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-gray-500">Candidate Name:</span>
                        <span className="ml-1 font-medium">{checkDetailData?.employee?.name || checkDetailData?.candidate_name || selectedCheck.employee_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Father's Name:</span>
                        <span className="ml-1 font-medium">{checkDetailData?.employee?.father_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Contact:</span>
                        <span className="ml-1 font-medium">{checkDetailData?.employee?.phone || selectedCheck.phone || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Email:</span>
                        <span className="ml-1 font-medium">{checkDetailData?.employee?.email || selectedCheck.employee_email || '-'}</span>
                      </div>
                      {/* Show addresses if available */}
                      {checkDetailData?.employee?.addresses?.length > 0 && (
                        <div className="col-span-2 mt-2">
                          <span className="text-gray-500 block mb-1">Addresses:</span>
                          {checkDetailData.employee.addresses.map((addr, idx) => (
                            <div key={idx} className="mb-2 p-2 bg-white rounded border">
                              <div className="font-medium text-amber-700">{addr.address_type || `Address ${idx + 1}`}</div>
                              <div className="text-gray-600">{addr.address_line1 || addr.complete_address || '-'}</div>
                              <div className="text-gray-500">{[addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Show current/permanent address fields if no addresses array */}
                      {(!checkDetailData?.employee?.addresses || checkDetailData.employee.addresses.length === 0) && (checkDetailData?.employee?.current_address || checkDetailData?.employee?.permanent_address) && (
                        <>
                          {checkDetailData.employee.current_address && (
                            <div className="col-span-2">
                              <span className="text-gray-500">Current Address:</span>
                              <div className="font-medium">{checkDetailData.employee.current_address}</div>
                            </div>
                          )}
                          {checkDetailData.employee.permanent_address && (
                            <div className="col-span-2">
                              <span className="text-gray-500">Permanent Address:</span>
                              <div className="font-medium">{checkDetailData.employee.permanent_address}</div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Loading indicator for check details */}
                {loadingDocs && !checkDetailData && (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mr-2" />
                    <span className="text-gray-500">Loading check details...</span>
                  </div>
                )}

                {/* Documents List */}

                {/* PAN Details for Credit Check / World Database / India Database - standalone section */}
                {(selectedCheck.check_type === 'credit_check' || selectedCheck.check_type === 'global_database' || selectedCheck.check_type === 'india_database') && (
                  <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
                    <h4 className="font-semibold mb-2 text-sm text-violet-800">PAN & Identity Details</h4>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-gray-500">PAN Number:</span>
                        <span className={`ml-1 font-medium ${!selectedCheck.pan_number ? 'text-red-500' : ''}`}>
                          {selectedCheck.pan_number || 'Not Available'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">PAN Card Copy:</span>
                        {selectedCheck.pan_card_url && selectedCheck.pan_card_url !== 'None' ? (
                          <a href={selectedCheck.pan_card_url.startsWith('http') ? selectedCheck.pan_card_url : `${axios.defaults.baseURL}${selectedCheck.pan_card_url}`} target="_blank" rel="noopener noreferrer"
                            className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 underline">
                            <Eye className="w-3 h-3" />View PAN Card
                          </a>
                        ) : (
                          <span className="ml-1 text-red-500 font-medium">Not Uploaded</span>
                        )}
                      </div>
                    </div>
                    {(selectedCheck.package_check_types || []).some(t => t.toLowerCase().includes('cibil') || t.toLowerCase().includes('credit')) && (!selectedCheck.pan_card_url || selectedCheck.pan_card_url === 'None') && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        <strong>PAN Card upload is mandatory</strong> — Client package includes CIBIL/Credit check. PAN card must be uploaded by data entry team.
                      </div>
                    )}
                  </div>
                )}

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold mb-2 text-sm text-blue-800">Documents ({checkDocuments.length})</h4>
                  {loadingDocs ? (
                    <div className="flex items-center justify-center py-4"><RefreshCw className="w-5 h-5 animate-spin text-blue-500" /></div>
                  ) : checkDocuments.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">No documents uploaded yet</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {checkDocuments.map((doc, idx) => (
                        <div key={doc.id || idx} className={`flex items-center justify-between p-2 rounded-md cursor-pointer text-xs ${previewDoc?.id === doc.id ? 'bg-blue-200 border-2 border-blue-400' : doc.document_type === 'declaration_form' ? 'bg-indigo-50 hover:bg-indigo-100 border border-indigo-200' : 'bg-white hover:bg-blue-100 border'}`}
                          onClick={() => setPreviewDoc(doc)}>
                          <span className="truncate">{doc.document_type === 'declaration_form' ? 'Declaration Form' : doc.document_type || doc.name || `Document ${idx + 1}`}</span>
                          <Eye className="w-3 h-3 text-gray-400" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Upload Verification Certificate - for non-field checks (Criminal Court, Criminal Advocate, ID, etc.) */}
                {selectedCheck.check_type !== 'address_check' && selectedCheck.check_type !== 'address_verification' && (
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold mb-2 text-sm text-emerald-800">Upload Verification Certificate</h4>
                    {selectedCheck.vendor_report_uploaded ? (
                      <div className="flex items-center gap-2 text-xs text-emerald-700 mb-2">
                        <CheckCircle className="w-4 h-4" />
                        <span>Report uploaded by {selectedCheck.vendor_report_uploaded_by || 'Vendor'}</span>
                      </div>
                    ) : null}
                    <div className="flex items-center gap-2">
                      <label className="flex-1 cursor-pointer">
                        <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${uploadingReport ? 'border-gray-300 bg-gray-50' : 'border-emerald-300 hover:border-emerald-500 hover:bg-emerald-100'}`}>
                          {uploadingReport ? (
                            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                              <RefreshCw className="w-4 h-4 animate-spin" />Uploading...
                            </div>
                          ) : (
                            <div className="text-xs text-emerald-700">
                              <Upload className="w-5 h-5 mx-auto mb-1" />
                              <p className="font-medium">Click to upload verification certificate</p>
                              <p className="text-emerald-500">PDF, JPG, PNG, DOC (max 10MB)</p>
                            </div>
                          )}
                        </div>
                        <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleUploadVerificationReport} disabled={uploadingReport} data-testid="upload-verification-report" />
                      </label>
                    </div>
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                      <strong>Note:</strong> Use <em>Bulk Upload</em> option for bulk update. Before upload, save documents by <strong>Case ID</strong> or <strong>Check ID</strong> for easy reference.
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side: Document Preview */}
              {previewDoc && (
                <div className="w-1/2 border-l pl-4 flex flex-col">
                  <div className="flex justify-between items-center mb-2 pb-2 border-b">
                    <div>
                      <h4 className="font-medium text-sm">{previewDoc.document_type || previewDoc.name}</h4>
                      <p className="text-xs text-gray-500">{formatDate(previewDoc.uploaded_at || previewDoc.created_at)}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setPreviewDoc(null)}><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                    {previewDoc.file_url || previewDoc.url ? (
                      previewDoc.file_type?.includes('image') || previewDoc.file_url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                        <img src={previewDoc.file_url || previewDoc.url} alt="Document" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <iframe src={previewDoc.file_url || previewDoc.url} className="w-full h-full" title="Document Preview" />
                      )
                    ) : (
                      <div className="text-center text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                        <p>Preview not available</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Verification Update Dialog - Comprehensive version */}
      {/* Feb 2026 — Agent Vendor Team can edit completed checks (all types)
          so they can fix wrong photos/annexures/details and re-submit. */}
      <VerificationUpdateDialog
        open={showUpdateStatus}
        onOpenChange={setShowUpdateStatus}
        check={selectedCheck}
        onSuccess={fetchData}
        allowEditWhenCompleted={true}
      />

      {/* Raise Insufficiency Dialog */}
      <Dialog open={showRaiseInsuff} onOpenChange={setShowRaiseInsuff}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Raise Insufficiency</DialogTitle></DialogHeader>
          {selectedCheck && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                <p className="text-sm"><span className="text-gray-500">Employee:</span> <span className="font-medium">{selectedCheck.employee_name}</span></p>
                <p className="text-sm"><span className="text-gray-500">Check Type:</span> <Badge variant="outline">{getCheckTypeLabel(selectedCheck.check_type)}</Badge></p>
                <p className="text-sm"><span className="text-gray-500">Case ID:</span> <span className="font-mono text-xs">{selectedCheck.case_id}</span></p>
              </div>
              <div className="space-y-2">
                <Label>Insufficiency Reason *</Label>
                <Select value={insuffData.reason} onValueChange={(v) => setInsuffData({...insuffData, reason: v})}>
                  <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="missing_documents">Missing Documents</SelectItem>
                    <SelectItem value="incorrect_information">Incorrect Information</SelectItem>
                    <SelectItem value="unverifiable_details">Unverifiable Details</SelectItem>
                    <SelectItem value="contact_not_reachable">Contact Not Reachable</SelectItem>
                    <SelectItem value="address_mismatch">Address Mismatch</SelectItem>
                    <SelectItem value="company_not_found">Company/Institution Not Found</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Additional Details</Label>
                <Textarea 
                  placeholder="Provide additional details about the insufficiency..."
                  value={insuffData.details}
                  onChange={(e) => setInsuffData({...insuffData, details: e.target.value})}
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowRaiseInsuff(false)}>Cancel</Button>
                <Button className="bg-red-600 hover:bg-red-700" onClick={handleRaiseInsuff}>Raise Insufficiency</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Extra Cost Request Dialog */}
      <Dialog open={showExtraCostDialog} onOpenChange={(open) => { setShowExtraCostDialog(open); if (!open) { setExtraCostAmount(''); setExtraCostRemarks(''); setExtraCostCheck(null); } }}>
        <DialogContent className="max-w-md" data-testid="extra-cost-dialog">
          <DialogHeader><DialogTitle className="text-amber-600 flex items-center gap-2"><CreditCard className="w-5 h-5" />Raise Extra Cost Request</DialogTitle></DialogHeader>
          {extraCostCheck && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                <p className="text-sm"><span className="text-gray-500">Employee:</span> <span className="font-medium">{extraCostCheck.employee_name}</span></p>
                <p className="text-sm"><span className="text-gray-500">Check:</span> <Badge variant="outline">{getCheckTypeLabel(extraCostCheck.check_type)}</Badge></p>
                <p className="text-sm"><span className="text-gray-500">Case:</span> <span className="font-mono text-xs">{extraCostCheck.case_id}</span></p>
              </div>
              <div className="space-y-2">
                <Label>Amount (Rs.) *</Label>
                <Input type="number" placeholder="Enter extra cost amount" value={extraCostAmount} onChange={(e) => setExtraCostAmount(e.target.value)} data-testid="extra-cost-amount" />
              </div>
              <div className="space-y-2">
                <Label>Reason / Remarks *</Label>
                <Textarea placeholder="Explain why extra cost is needed..." value={extraCostRemarks} onChange={(e) => setExtraCostRemarks(e.target.value)} rows={3} data-testid="extra-cost-remarks" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowExtraCostDialog(false)}>Cancel</Button>
                <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleRaiseExtraCost} disabled={!extraCostAmount || !extraCostRemarks || extraCostLoading} data-testid="extra-cost-submit">
                  {extraCostLoading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Return to Ops Manager Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-amber-600 flex items-center gap-2"><RotateCcw className="w-5 h-5" />Return to Ops Manager</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg space-y-1">
              <p className="text-sm"><span className="text-gray-500">Employee:</span> <span className="font-medium">{selectedCheck?.employee_name}</span></p>
              <p className="text-sm"><span className="text-gray-500">Check:</span> <Badge variant="outline">{selectedCheck?.check_type}</Badge></p>
            </div>
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Select value={returnData.reason} onValueChange={(v) => setReturnData({...returnData, reason: v})}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="incorrect_assignment">Incorrect Assignment</SelectItem>
                  <SelectItem value="out_of_coverage">Out of Coverage Area</SelectItem>
                  <SelectItem value="capacity_issue">Capacity Issue</SelectItem>
                  <SelectItem value="data_issue">Data / Document Issue</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea placeholder="Additional notes..." value={returnData.notes} onChange={(e) => setReturnData({...returnData, notes: e.target.value})} rows={3} />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowReturnDialog(false)}>Cancel</Button>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleReturnCheck} disabled={!returnData.reason}>Return Check</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Initiate Dialog */}
      <Dialog open={showBulkInitiateDialog} onOpenChange={(open) => { 
        setShowBulkInitiateDialog(open); 
        if (!open) { 
          setBulkInitiateVendorId(''); 
          setBulkInitiateAutoAssign(true);
          setBulkFieldAgentForm({ name: '', phone: '', pincode: '' });
          setShowBulkFieldAgentSection(false);
          setBulkSpecialInstructions('');
        } 
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Send className="w-5 h-5" />Bulk Initiate & Assign Verification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-800">
                You are about to process <span className="font-bold">{selectedChecks.length}</span> selected check(s).
              </p>
            </div>
            
            {/* Special Instructions Box */}
            <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
              <div className="flex items-center gap-2 text-amber-700 font-semibold mb-2">
                <AlertTriangle className="w-4 h-4" />
                Special Instructions
              </div>
              <Textarea
                placeholder="Enter any special instructions for field verification..."
                value={bulkSpecialInstructions}
                onChange={(e) => setBulkSpecialInstructions(e.target.value)}
                className="bg-white min-h-[80px] text-sm"
                data-testid="bulk-special-instructions"
              />
              <p className="text-xs text-amber-600 mt-2">
                These instructions will be visible to the field agent on the verification page.
              </p>
            </div>
            
            {/* Select Vendor to Assign Checks - Primary action */}
            {user?.role !== 'external_vendor' && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
                <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Select Vendor to Assign Checks
                </p>
                <Select value={bulkInitiateVendorId} onValueChange={setBulkInitiateVendorId}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Choose a vendor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        <div className="flex items-center gap-2">
                          <span>{v.vendor_name || v.name}</span>
                          {v.city && <span className="text-xs text-gray-500">({v.city})</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-blue-600">
                  All selected checks will be assigned to this vendor for verification.
                </p>
              </div>
            )}

            {/* Auto-Assign & Field Agent section */}
            <div className="space-y-3">
              {/* Field Agent Assignment Toggle - Primary action for vendors */}
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div>
                  <p className="text-sm font-medium text-purple-800 flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Assign Field Agent
                  </p>
                  <p className="text-xs text-purple-600">Assign same field agent to all selected checks</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${showBulkFieldAgentSection ? 'text-purple-600' : 'text-gray-400'}`}>
                    {showBulkFieldAgentSection ? 'ON' : 'OFF'}
                  </span>
                  <Checkbox 
                    checked={showBulkFieldAgentSection} 
                    onCheckedChange={setShowBulkFieldAgentSection}
                    className="data-[state=checked]:bg-purple-600"
                  />
                </div>
              </div>
              
              {/* Field Agent Form (shown when toggle is ON) */}
              {showBulkFieldAgentSection && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm text-purple-800">Field Agent Name *</Label>
                    <Input 
                      placeholder="Enter or select field agent name"
                      value={bulkFieldAgentForm.name}
                      onChange={(e) => setBulkFieldAgentForm({...bulkFieldAgentForm, name: e.target.value})}
                      list="bulk-field-agents-list"
                      className="bg-white"
                    />
                    <datalist id="bulk-field-agents-list">
                      {fieldAgents.map(fa => (
                        <option key={fa.id || fa.name} value={fa.name} />
                      ))}
                    </datalist>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-purple-800">Phone Number</Label>
                    <Input 
                      placeholder="Field agent phone number"
                      value={bulkFieldAgentForm.phone}
                      onChange={(e) => setBulkFieldAgentForm({...bulkFieldAgentForm, phone: e.target.value})}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-purple-800">Area Pincode</Label>
                    <Input 
                      placeholder="Pincode for search"
                      value={bulkFieldAgentForm.pincode}
                      onChange={(e) => setBulkFieldAgentForm({...bulkFieldAgentForm, pincode: e.target.value})}
                      className="bg-white"
                    />
                  </div>
                  <p className="text-xs text-purple-600">
                    Verification links will be copied to clipboard after assignment.
                  </p>
                </div>
              )}

              {/* Auto-Assign Mode - Optional, default OFF */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Auto-Assign Mode</p>
                  <p className="text-xs text-gray-500">Auto-assign field agents by pincode matching</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${bulkInitiateAutoAssign ? 'text-green-600' : 'text-gray-400'}`}>
                    {bulkInitiateAutoAssign ? 'ON' : 'OFF'}
                  </span>
                  <Checkbox 
                    checked={bulkInitiateAutoAssign} 
                    onCheckedChange={setBulkInitiateAutoAssign}
                    className="data-[state=checked]:bg-green-600"
                  />
                </div>
              </div>
              
              {/* Auto-assign info note */}
              {bulkInitiateAutoAssign && (
                <div className="p-2 bg-green-50 rounded-lg border border-green-200 text-xs text-green-700">
                  <p className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Checks will be auto-assigned to field agents based on pincode matching.
                    <button 
                      onClick={() => setActiveTab('field-agents')}
                      className="text-green-800 underline ml-1"
                    >
                      Manage pincodes
                    </button>
                  </p>
                </div>
              )}
            </div>
            
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
              {showBulkFieldAgentSection 
                ? 'This will assign the field agent to all selected checks and copy verification links.'
                : bulkInitiateAutoAssign 
                  ? 'This will mark the checks as "In Progress" and send notifications to relevant parties.'
                  : 'This will assign all selected checks to the chosen vendor and mark them as "Vendor Assigned".'
              }
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowBulkInitiateDialog(false)} disabled={bulkInitiateLoading}>Cancel</Button>
              <Button 
                className={showBulkFieldAgentSection ? "bg-purple-600 hover:bg-purple-700" : "bg-green-600 hover:bg-green-700"}
                onClick={handleBulkInitiate} 
                disabled={bulkInitiateLoading || (!bulkInitiateAutoAssign && !bulkInitiateVendorId && !showBulkFieldAgentSection && user?.role !== 'external_vendor') || (showBulkFieldAgentSection && !bulkFieldAgentForm.name.trim())}
              >
                {bulkInitiateLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                ) : showBulkFieldAgentSection ? (
                  <><Share2 className="w-4 h-4 mr-2" />Assign & Copy Links ({selectedChecks.length})</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />{bulkInitiateAutoAssign ? 'Initiate' : 'Assign to Vendor'} ({selectedChecks.length})</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Document Upload Dialog */}

      {/* Transfer to Vendor Team Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={(open) => {
        setShowTransferDialog(open);
        if (!open) { setTransferAgentId(''); setTransferReason(''); }
      }}>
        <DialogContent className="max-w-lg" data-testid="transfer-to-vendor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Truck className="w-5 h-5" />Transfer to Vendor Team Agent
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-800">
                Transfer <span className="font-bold">{selectedChecks.length}</span> selected check(s) to an internal vendor team agent.
              </p>
              <p className="text-xs text-purple-600 mt-1">This bypasses the Ops Manager and assigns checks directly to the selected team member.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Select Vendor Team Agent *</Label>
              <Select value={transferAgentId} onValueChange={setTransferAgentId}>
                <SelectTrigger data-testid="transfer-vendor-select"><SelectValue placeholder="Choose an agent..." /></SelectTrigger>
                <SelectContent>
                  {vendorTeamAgents.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      <div className="flex items-center gap-2">
                        <span>{a.name}</span>
                        <span className="text-xs text-gray-500">({a.email})</span>
                        {a.assigned_check_types?.length > 0 && (
                          <span className="text-xs text-purple-600 ml-1">[{a.assigned_check_types.join(', ')}]</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Reason / Instructions</Label>
              <Textarea
                placeholder="Enter reason for transfer or special instructions..."
                value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)}
                className="min-h-[80px] text-sm"
                data-testid="transfer-reason-input"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowTransferDialog(false)} disabled={transferLoading}>Cancel</Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={handleTransferToVendor}
                disabled={transferLoading || !transferAgentId}
                data-testid="transfer-confirm-btn"
              >
                {transferLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Transferring...</>
                ) : (
                  <><Truck className="w-4 h-4 mr-2" />Transfer ({selectedChecks.length})</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Document Upload</DialogTitle>
          </DialogHeader>
          <BulkDocumentUpload
            onClose={() => {
              setShowBulkUpload(false);
              fetchData();
            }}
            user={user}
          />
        </DialogContent>
      </Dialog>

      {/* Field Agent Assignment Dialog */}
      <Dialog open={showAssignFieldAgent} onOpenChange={(open) => {
        setShowAssignFieldAgent(open);
        if (!open) setCopiedText(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              {copiedText ? 'Copy Verification Details' : 'Assign Field Agent'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {copiedText ? (
              /* Show generated text for manual copy */
              <div className="space-y-3">
                <p className="text-sm text-green-700 font-medium">Field Agent assigned successfully! Copy the details below to share via WhatsApp:</p>
                <textarea
                  readOnly
                  value={copiedText}
                  className="w-full h-64 p-3 text-xs font-mono bg-slate-50 border rounded-lg resize-none focus:outline-none"
                  onClick={(e) => e.target.select()}
                  data-testid="copied-text-area"
                />
                <div className="flex gap-2">
                  <Button 
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={async () => {
                      const ok = await safeClipboardWrite(copiedText);
                      if (ok) {
                        toast.success('Copied to clipboard!');
                        setCopiedText(null);
                        setShowAssignFieldAgent(false);
                        setFieldAgentForm({ name: '', phone: '', pincode: '' });
                      } else {
                        toast.info('Please select all text above and copy manually (Ctrl+C)');
                      }
                    }}
                    data-testid="copy-details-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy to Clipboard
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setCopiedText(null);
                      setShowAssignFieldAgent(false);
                      setFieldAgentForm({ name: '', phone: '', pincode: '' });
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              /* Normal assign form */
              <>
            {selectedCheckForFA && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <div className="font-medium">{selectedCheckForFA.employee_name}</div>
                <div className="text-gray-500 text-xs">
                  Case: {selectedCheckForFA.case_id} | Check: {selectedCheckForFA.check_id || selectedCheckForFA.id}
                </div>
                <div className="text-gray-500 text-xs mt-1">
                  {selectedCheckForFA.check_type?.replace(/_/g, ' ').toUpperCase()}
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Field Agent Name *</Label>
                <div className="relative">
                  <Input
                    placeholder="Enter or select field agent name"
                    value={fieldAgentForm.name}
                    onChange={(e) => setFieldAgentForm({...fieldAgentForm, name: e.target.value})}
                    list="field-agent-list"
                  />
                  <datalist id="field-agent-list">
                    {fieldAgents.map(fa => (
                      <option key={fa.id} value={fa.name}>{fa.name} - {fa.phone}</option>
                    ))}
                  </datalist>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input
                  placeholder="Field agent phone number"
                  value={fieldAgentForm.phone}
                  onChange={(e) => setFieldAgentForm({...fieldAgentForm, phone: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Area Pincode</Label>
                <Input
                  placeholder="Pincode for search"
                  value={fieldAgentForm.pincode}
                  onChange={(e) => setFieldAgentForm({...fieldAgentForm, pincode: e.target.value})}
                />
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAssignFieldAgent(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={handleAssignFieldAgent}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Assign & Copy Link
              </Button>
            </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Field Verification View Dialog */}
      <Dialog open={showFieldVerificationView} onOpenChange={setShowFieldVerificationView}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="w-5 h-5 text-indigo-600" />
              View Updated Form - Field Verification Details
            </DialogTitle>
          </DialogHeader>
          {fieldVerificationData && (
            <div className="space-y-4 py-2">
              {/* Case Info */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Case Information
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-gray-500">Case ID:</span> <span className="font-medium">{fieldVerificationData.case_id}</span></div>
                  <div><span className="text-gray-500">Check ID:</span> <span className="font-medium">{fieldVerificationData.check_id || fieldVerificationData.id}</span></div>
                  <div><span className="text-gray-500">Employee:</span> <span className="font-medium">{fieldVerificationData.employee_name}</span></div>
                  <div><span className="text-gray-500">Check Type:</span> <span className="font-medium capitalize">{fieldVerificationData.check_type?.replace(/_/g, ' ')}</span></div>
                  <div><span className="text-gray-500">Client:</span> <span className="font-medium">{fieldVerificationData.client_name || '-'}</span></div>
                  <div><span className="text-gray-500">Status:</span> <Badge className={fieldVerificationData.field_verification?.submitted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{fieldVerificationData.field_verification?.submitted ? 'Submitted' : 'Pending'}</Badge></div>
                  <div><span className="text-gray-500">Submitted At:</span> <span className="font-medium">{fieldVerificationData.field_verification?.submitted_at ? new Date(fieldVerificationData.field_verification.submitted_at).toLocaleString() : '-'}</span></div>
                </div>
              </div>

              {/* Address Details - For Address Checks */}
              {fieldVerificationData.check_type?.toLowerCase().includes('address') && (
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <h4 className="font-medium text-sm mb-3 text-indigo-800 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Address Details
                    <Badge className={
                      fieldVerificationData.subcheck_data?.address_type?.toLowerCase().includes('present') ||
                      fieldVerificationData.address_type?.toLowerCase().includes('present') || 
                      fieldVerificationData.subcheck_type?.toLowerCase().includes('present') ||
                      fieldVerificationData.check_index === 1
                        ? 'bg-blue-100 text-blue-700 ml-2' 
                        : 'bg-purple-100 text-purple-700 ml-2'
                    }>
                      {fieldVerificationData.subcheck_data?.address_type?.toLowerCase().includes('present') ||
                       fieldVerificationData.address_type?.toLowerCase().includes('present') || 
                       fieldVerificationData.subcheck_type?.toLowerCase().includes('present') ||
                       fieldVerificationData.check_index === 1
                        ? 'Present Address' 
                        : fieldVerificationData.subcheck_data?.address_type?.toLowerCase().includes('permanent') ||
                          fieldVerificationData.address_type?.toLowerCase().includes('permanent') ||
                          fieldVerificationData.subcheck_type?.toLowerCase().includes('permanent') ||
                          fieldVerificationData.check_index === 2
                          ? 'Permanent Address'
                          : `Address ${fieldVerificationData.check_index || ''}`}
                    </Badge>
                  </h4>
                  <div className="bg-white p-3 rounded border border-indigo-100">
                    <p className="text-sm font-medium text-gray-800">
                      {/* Use subcheck_data which has the address from case initiation */}
                      {fieldVerificationData.subcheck_data?.complete_address ||
                       fieldVerificationData.subcheck_data?.address_line1 ||
                       fieldVerificationData.subcheck_location ||
                       fieldVerificationData.verified_details?.complete_address ||
                       [
                        fieldVerificationData.subcheck_data?.address_line1,
                        fieldVerificationData.subcheck_data?.address_line2,
                        fieldVerificationData.subcheck_data?.landmark,
                        fieldVerificationData.subcheck_data?.city,
                        fieldVerificationData.subcheck_data?.state,
                        fieldVerificationData.subcheck_data?.pincode
                       ].filter(Boolean).join(', ') || 
                       'Address details not available'}
                    </p>
                    {fieldVerificationData.subcheck_data && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs">
                        {fieldVerificationData.subcheck_data.city && (
                          <div><span className="text-gray-500">City:</span> <span className="font-medium">{fieldVerificationData.subcheck_data.city}</span></div>
                        )}
                        {fieldVerificationData.subcheck_data.state && (
                          <div><span className="text-gray-500">State:</span> <span className="font-medium">{fieldVerificationData.subcheck_data.state}</span></div>
                        )}
                        {fieldVerificationData.subcheck_data.pincode && (
                          <div><span className="text-gray-500">Pincode:</span> <span className="font-medium">{fieldVerificationData.subcheck_data.pincode}</span></div>
                        )}
                        {fieldVerificationData.subcheck_data.landmark && (
                          <div><span className="text-gray-500">Landmark:</span> <span className="font-medium">{fieldVerificationData.subcheck_data.landmark}</span></div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Field Agent Info */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-medium text-sm mb-3 text-purple-800 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Field Agent Details
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-purple-600 text-xs mb-1">Verifier Name</p>
                    <p className="font-medium">{fieldVerificationData.field_agent_name || fieldVerificationData.field_verification?.verifier_name || fieldVerificationData.field_verification?.person_met_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-purple-600 text-xs mb-1">Contact Number</p>
                    <p className="font-medium">{fieldVerificationData.field_agent_phone || fieldVerificationData.field_verification?.verifier_contact || fieldVerificationData.field_verification?.person_met_contact || '-'}</p>
                  </div>
                  <div>
                    <p className="text-purple-600 text-xs mb-1">Date of Visit</p>
                    <p className="font-medium">
                      {fieldVerificationData.field_verification?.date_of_visit || 
                       (fieldVerificationData.field_verification?.submitted_at ? new Date(fieldVerificationData.field_verification.submitted_at).toLocaleDateString() : '-')}
                    </p>
                  </div>
                  <div>
                    <p className="text-purple-600 text-xs mb-1">Time of Visit</p>
                    <p className="font-medium">
                      {fieldVerificationData.field_verification?.time_of_visit || 
                       (fieldVerificationData.field_verification?.submitted_at ? new Date(fieldVerificationData.field_verification.submitted_at).toLocaleTimeString() : '-')}
                    </p>
                  </div>
                </div>
              </div>

              {/* GPS & Location */}
              {fieldVerificationData.field_verification?.gps_coordinates && (
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <h4 className="font-medium text-sm mb-2 text-emerald-800 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    GPS Location
                  </h4>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-mono bg-white px-3 py-1 rounded border">{fieldVerificationData.field_verification.gps_coordinates}</span>
                    <a 
                      href={`https://maps.google.com/?q=${fieldVerificationData.field_verification.gps_coordinates}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-700 text-sm underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> View on Map
                    </a>
                  </div>
                </div>
              )}

              {/* Verification Details */}
              {fieldVerificationData.field_verification && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-sm mb-3 text-blue-800 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Verifier Details
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Residence Confirmed</p>
                      <Badge className={fieldVerificationData.field_verification.residence_confirmed === 'yes' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {fieldVerificationData.field_verification.residence_confirmed || '-'}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Type of Residence</p>
                      <p className="font-medium capitalize">{fieldVerificationData.field_verification.residence_type?.replace(/_/g, ' ') || fieldVerificationData.field_verification.residence_status || '-'}</p>
                    </div>
                    <div>
                      <p className="text-blue-600 text-xs mb-1">House/Building Type</p>
                      <p className="font-medium capitalize">{fieldVerificationData.field_verification.building_type?.replace(/_/g, ' ') || '-'}</p>
                    </div>
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Locality Type</p>
                      <p className="font-medium capitalize">{fieldVerificationData.field_verification.locality_type?.replace(/_/g, ' ') || '-'}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3 pt-3 border-t border-blue-200">
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Period of Stay From</p>
                      <p className="font-medium">{fieldVerificationData.field_verification.period_of_stay_from || fieldVerificationData.field_verification.period_of_stay || '-'}</p>
                    </div>
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Period of Stay To</p>
                      <p className="font-medium">{fieldVerificationData.field_verification.period_of_stay_to || 'Present'}</p>
                    </div>
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Neighbour Confirmed</p>
                      <Badge className={fieldVerificationData.field_verification.neighbour_confirmed === 'yes' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {fieldVerificationData.field_verification.neighbour_confirmed || fieldVerificationData.field_verification.neighbor_confirmed || '-'}
                      </Badge>
                    </div>
                    {(fieldVerificationData.field_verification.neighbour_confirmed === 'yes' || fieldVerificationData.field_verification.neighbor_confirmed === 'yes') && fieldVerificationData.field_verification.neighbor_name && (
                      <div>
                        <p className="text-blue-600 text-xs mb-1">Neighbour Name</p>
                        <p className="font-medium">{fieldVerificationData.field_verification.neighbor_name}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Person Met Details */}
                  {fieldVerificationData.field_verification.person_met_name && (
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <h5 className="font-medium text-xs mb-3 text-blue-700">Person Met at Location</h5>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-blue-600 text-xs mb-1">Name</p>
                          <p className="font-medium">{fieldVerificationData.field_verification.person_met_name}</p>
                        </div>
                        <div>
                          <p className="text-blue-600 text-xs mb-1">Relation</p>
                          <p className="font-medium capitalize">{fieldVerificationData.field_verification.person_met_relation || '-'}</p>
                        </div>
                        <div>
                          <p className="text-blue-600 text-xs mb-1">Contact</p>
                          <p className="font-medium">{fieldVerificationData.field_verification.person_met_contact || '-'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Remarks - Always show if present */}
              {(fieldVerificationData.field_verification?.overall_remarks || fieldVerificationData.field_verification?.remarks) && (
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-sm mb-2 text-amber-800 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Field Agent Remarks
                  </h4>
                  <p className="text-sm bg-white p-3 rounded border border-amber-200">{fieldVerificationData.field_verification.overall_remarks || fieldVerificationData.field_verification.remarks}</p>
                </div>
              )}

              {/* Ethics Declaration */}
              {fieldVerificationData.field_verification?.ethics_declaration && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Ethics Declaration Accepted</p>
                    <p className="text-sm text-green-600">Field agent has accepted the ethics and compliance declaration</p>
                  </div>
                </div>
              )}

              {/* Photos Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Verification Photos
                </h4>
                
                {/* Photos from field_verification.photos */}
                {fieldVerificationData.field_verification?.photos && fieldVerificationData.field_verification.photos.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">Field Verification Photos ({fieldVerificationData.field_verification.photos.length})</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {fieldVerificationData.field_verification.photos.map((photo, idx) => {
                        // Handle different photo formats - could be string URL, object with url/file_url, or reference {id, type}
                        let photoUrl = '';
                        let photoLabel = `Photo ${idx + 1}`;
                        let photoGps = null;
                        if (typeof photo === 'string') {
                          photoUrl = photo;
                        } else if (photo && typeof photo === 'object') {
                          photoUrl = photo.url || photo.file_url || '';
                          photoLabel = photo.label || photo.type?.replace(/_/g, ' ') || photoLabel;
                          photoGps = photo.gps_coordinates || (photo.gps_latitude && photo.gps_longitude ? `${photo.gps_latitude}, ${photo.gps_longitude}` : null);
                          // Resolve from annexures if only id/type reference
                          if (!photoUrl && photo.id && fieldVerificationPhotos.length > 0) {
                            const match = fieldVerificationPhotos.find(a => a.id === photo.id);
                            if (match) {
                              photoUrl = match.file_url || match.url || '';
                              if (!photoGps && (match.gps_latitude || match.gps_longitude)) {
                                photoGps = `${match.gps_latitude}, ${match.gps_longitude}`;
                              }
                              if (match.description) photoLabel = match.description;
                            }
                          }
                        }
                        const isValidUrl = photoUrl && typeof photoUrl === 'string' && (photoUrl.startsWith('http') || photoUrl.startsWith('data:'));
                        return (
                          <div 
                            key={idx} 
                            className="relative group cursor-pointer" 
                            onClick={() => {
                              if (isValidUrl) {
                                setPhotoPreview({
                                  url: photoUrl,
                                  label: photoLabel,
                                  gps: photoGps
                                });
                                setShowPhotoPreview(true);
                              } else {
                                toast.error('Photo URL is invalid or not available');
                              }
                            }}
                          >
                            {isValidUrl ? (
                              <img 
                                src={photoUrl} 
                                alt={photoLabel}
                                className="w-full h-28 object-cover rounded-lg border-2 border-gray-200 hover:border-indigo-400 transition-colors"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                }}
                              />
                            ) : (
                              <div className="w-full h-28 bg-gray-200 rounded-lg border-2 border-gray-300 flex items-center justify-center">
                                <Camera className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1.5 rounded-b-lg text-center">
                              {photoLabel}
                            </div>
                            {photoGps && (
                              <div className="absolute top-1 right-1 bg-green-500 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">
                                GPS ✓
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Photos from annexures API */}
                {fieldVerificationPhotos.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Uploaded Documents ({fieldVerificationPhotos.length})</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {fieldVerificationPhotos.map((photo, idx) => {
                        const photoUrl = photo.file_url || photo.url;
                        const isValidUrl = photoUrl && (photoUrl.startsWith('http') || photoUrl.startsWith('data:'));
                        return (
                          <div 
                            key={photo.id || idx} 
                            className="relative group cursor-pointer" 
                            onClick={() => {
                              if (isValidUrl) {
                                setPhotoPreview({
                                  url: photoUrl,
                                  label: photo.annexure_type?.replace(/_/g, ' ') || photo.description || `Photo ${idx + 1}`,
                                  gps: photo.gps_latitude && photo.gps_longitude ? `${photo.gps_latitude}, ${photo.gps_longitude}` : null
                                });
                                setShowPhotoPreview(true);
                              } else {
                                toast.error('Photo URL is invalid or not available');
                              }
                            }}
                          >
                            {isValidUrl ? (
                              <img 
                                src={photoUrl} 
                                alt={photo.annexure_type || `Photo ${idx + 1}`}
                                className="w-full h-28 object-cover rounded-lg border-2 border-gray-200 hover:border-indigo-400 transition-colors"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                }}
                              />
                            ) : (
                              <div className="w-full h-28 bg-gray-200 rounded-lg border-2 border-gray-300 flex items-center justify-center">
                                <Camera className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1.5 rounded-b-lg text-center">
                              {photo.annexure_type?.replace(/_/g, ' ') || `Photo ${idx + 1}`}
                            </div>
                            {photo.gps_latitude && photo.gps_longitude && (
                              <div className="absolute top-1 right-1 bg-green-500 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">
                                GPS ✓
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* No Photos */}
                {(!fieldVerificationData.field_verification?.photos || fieldVerificationData.field_verification.photos.length === 0) && fieldVerificationPhotos.length === 0 && (
                  <div className="text-center py-6 text-gray-400">
                    <Camera className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No photos uploaded yet</p>
                  </div>
                )}
              </div>

              {/* No Data Message */}
              {!fieldVerificationData.field_verification?.submitted && fieldVerificationPhotos.length === 0 && (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p className="font-medium text-gray-600">Field verification not yet submitted</p>
                  <p className="text-sm text-gray-400 mt-1">The field agent has not completed the verification form</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => window.open(generateFieldLink(selectedCheck), '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />Open Field Form
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Preview Modal */}
      {showPhotoPreview && photoPreview && (
        <div 
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setShowPhotoPreview(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="font-semibold text-gray-800">{photoPreview.label}</h3>
                {photoPreview.gps && (
                  <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    GPS: {photoPreview.gps}
                  </p>
                )}
              </div>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => setShowPhotoPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 bg-gray-100 flex items-center justify-center" style={{ maxHeight: 'calc(90vh - 140px)' }}>
              <img 
                src={photoPreview.url} 
                alt={photoPreview.label}
                className="max-w-full max-h-[65vh] object-contain rounded shadow-lg"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-size="18"%3EImage failed to load%3C/text%3E%3C/svg%3E';
                }}
              />
            </div>
            <div className="p-4 border-t flex justify-between items-center bg-gray-50">
              <Button 
                variant="outline" 
                onClick={() => setShowPhotoPreview(false)}
              >
                Close
              </Button>
              <Button 
                variant="default"
                onClick={() => window.open(photoPreview.url, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in New Tab
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Field Agent Dialog */}
      <Dialog open={showAddAgentDialog} onOpenChange={setShowAddAgentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-indigo-600" />
              Add New Field Agent
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="Enter field agent name"
                value={newAgentForm.name}
                onChange={(e) => setNewAgentForm({...newAgentForm, name: e.target.value})}
                data-testid="new-agent-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                placeholder="Enter phone number"
                value={newAgentForm.phone}
                onChange={(e) => setNewAgentForm({...newAgentForm, phone: e.target.value})}
                data-testid="new-agent-phone-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Pincodes</Label>
              <Input
                placeholder="Enter pincodes (comma separated, e.g., 560001, 560002)"
                value={newAgentForm.pincodes}
                onChange={(e) => setNewAgentForm({...newAgentForm, pincodes: e.target.value})}
                data-testid="new-agent-pincodes-input"
              />
              <p className="text-xs text-gray-500">Enter multiple pincodes separated by commas to assign coverage areas</p>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAddAgentDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                onClick={handleCreateFieldAgent}
                data-testid="create-field-agent-btn"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Create Field Agent
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Urgent TAT Reminder Dialog — May 22, 2026 */}
      <Dialog
        open={showUrgentReminderDialog}
        onOpenChange={(o) => {
          if (!o) { setShowUrgentReminderDialog(false); setUrgentReminderSingleCheck(null); setUrgentReminderResult(null); }
        }}
      >
        <DialogContent className="max-w-xl" data-testid="urgent-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Send className="w-5 h-5" />
              Send Urgent TAT Reminder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 text-red-900">
              <p className="font-semibold text-sm mb-1">⚠ This will email the assigned vendor(s) with a strong escalation banner:</p>
              <p className="text-xs italic">"Need below TAT crossed report urgently or face penalty"</p>
              <p className="text-xs mt-2 text-red-800">
                Use this only for TAT-crossed or TAT-closing checks. The email uses the same table format as the daily scheduled reminder; CC includes the admin team, the agent who assigned the check, and you.
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p>
                Sending reminder for{' '}
                <strong>{urgentReminderSingleCheck ? 1 : selectedChecks.length}</strong>{' '}
                check(s). The system will group by vendor — one email per vendor with all selected rows.
              </p>
            </div>
            {urgentReminderResult && (
              <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 text-sm" data-testid="urgent-reminder-result">
                <div className="font-semibold text-emerald-800">{urgentReminderResult.message}</div>
                {Array.isArray(urgentReminderResult.sent_breakdown) && urgentReminderResult.sent_breakdown.length > 0 && (
                  <ul className="mt-2 text-xs list-disc ml-5">
                    {urgentReminderResult.sent_breakdown.map((s, i) => (
                      <li key={i}>{s.vendor_name} ({s.vendor_email}) — {s.checks_count} check(s), {s.cc_count} CC</li>
                    ))}
                  </ul>
                )}
                {Array.isArray(urgentReminderResult.skipped) && urgentReminderResult.skipped.length > 0 && (
                  <div className="mt-2 text-xs text-amber-800">
                    Skipped: {urgentReminderResult.skipped.map((s) => `${s.case_id || s.vendor_id} (${s.reason})`).join(' · ')}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowUrgentReminderDialog(false); setUrgentReminderSingleCheck(null); }}
              disabled={urgentReminderSending}
              data-testid="urgent-reminder-cancel-btn"
            >
              {urgentReminderResult ? 'Close' : 'Cancel'}
            </Button>
            {!urgentReminderResult && (
              <Button
                className="bg-red-600 hover:bg-red-700"
                disabled={urgentReminderSending}
                onClick={() => sendUrgentReminder(urgentReminderSingleCheck)}
                data-testid="urgent-reminder-confirm-send-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {urgentReminderSending ? 'Sending…' : 'Send Urgent Reminder'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
